import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Upload, Eye, Download, Calendar, Pin } from 'lucide-react';

// مكون رسالة الحقل
const FieldMessage = ({ message, type, isVisible }) => {
    if (!isVisible || !message) return null;
    
    const getMessageStyle = () => {
        switch(type) {
            case 'error':
            case 'invalid':
                return 'text-red-600 bg-red-50 border-red-200';
            case 'success':
            case 'valid':
                return 'text-green-600 bg-green-50 border-green-200';
            case 'checking':
                return 'text-blue-600 bg-blue-50 border-blue-200';
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getIcon = () => {
        switch(type) {
            case 'error':
            case 'invalid':
                return '❌';
            case 'success':
            case 'valid':
                return '✅';
            case 'checking':
                return '🔄';
            default:
                return 'ℹ️';
        }
    };

    return (
        <div className={`mt-1 px-3 py-2 rounded-md border text-sm font-medium flex items-center gap-2 ${getMessageStyle()}`}>
            <span>{getIcon()}</span>
            <span>{message}</span>
            {type === 'checking' && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            )}
        </div>
    );
};

// مكون الملاحظات
const NotesSection = ({ notes, setNotes, isViewMode }) => {
    const addNote = () => {
        if (isViewMode) return;
        const newNote = {
            id: Date.now(),
            text: "",
            date: new Date().toISOString(),
            user: "المستخدم الحالي",
        };
        setNotes([...notes, newNote]);
    };

    const updateNote = (id, text) => {
        if (isViewMode) return;
        setNotes(
            notes.map((note) =>
                note.id === id ? { ...note, text } : note
            )
        );
    };

    const deleteNote = (id) => {
        if (isViewMode) return;
        setNotes(notes.filter((note) => note.id !== id));
    };

    return (
        <div className="p-2">
            <div className="flex items-center mb-2">
                <span className="text-base font-bold mr-2">الملاحظات</span>
                {!isViewMode && (
                    <button
                        type="button"
                        onClick={addNote}
                        className="ml-auto bg-blue-600 text-white px-2 py-1 rounded flex items-center"
                        style={{ minWidth: 100 }}
                    >
                        <Plus size={16} className="ml-1" />
                        إضافة
                    </button>
                )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {notes.map((note) => (
                    <div
                        key={note.id}
                        className="bg-white border border-gray-200 rounded p-3 relative"
                        style={{ minHeight: 120 }}
                    >
                        <div className="mb-2">
                            <textarea
                                value={note.text}
                                onChange={(e) => updateNote(note.id, e.target.value)}
                                className="w-full bg-transparent border-0 focus:ring-0 text-sm resize-none"
                                style={{ minHeight: 60 }}
                                readOnly={isViewMode}
                                placeholder="اكتب الملاحظة..."
                                dir="rtl"
                            />
                        </div>
                        
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-400">
                                {new Date(note.date).toLocaleDateString("ar-EG")}
                            </span>
                            
                            {!isViewMode && notes.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => deleteNote(note.id)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                    title="حذف الملاحظة"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                
                {notes.length === 0 && (
                    <div className="col-span-full text-center text-gray-400 py-8">
                        <div className="text-lg mb-2">📝</div>
                        <p>لا توجد ملاحظات حتى الآن</p>
                        {!isViewMode && (
                            <p className="text-sm mt-1">انقر على "إضافة" لإنشاء ملاحظة جديدة</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// مكون عارض الصور

// ===== مكون اقتصاص الصور =====
export const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete, title = 'اقتصاص الصورة' }) => {
    const containerRef = React.useRef(null);
    const imageRef     = React.useRef(null);
    const [blobUrl,    setBlobUrl]   = React.useState(null);
    const [imgLoaded,  setImgLoaded] = React.useState(false);
    const [imgRect,    setImgRect]   = React.useState({ x:0, y:0, w:0, h:0 });
    const [crop, setCrop] = React.useState({ x:0, y:0, w:0, h:0 });
    const [drag, setDrag] = React.useState(null);

    React.useEffect(() => {
        if (!isOpen || !imageSrc) return;
        setImgLoaded(false); setBlobUrl(null); setCrop({x:0,y:0,w:0,h:0});
        fetch(imageSrc, { credentials:'include' })
            .then(r => r.blob()).then(blob => setBlobUrl(URL.createObjectURL(blob)))
            .catch(() => setBlobUrl(imageSrc));
    }, [isOpen, imageSrc]);

    React.useEffect(() => {
        return () => { if (blobUrl?.startsWith('blob:')) URL.revokeObjectURL(blobUrl); };
    }, [blobUrl]);

    const calcImgRect = () => {
        const img = imageRef.current, con = containerRef.current;
        if (!img || !con) return null;
        const ci = con.getBoundingClientRect(), ii = img.getBoundingClientRect();
        const r = { x: ii.left-ci.left, y: ii.top-ci.top, w: ii.width, h: ii.height };
        setImgRect(r);
        return r;
    };

    const handleImageLoad = () => {
        setImgLoaded(true);
        setTimeout(() => {
            const r = calcImgRect();
            if (r) setCrop({ x:r.x, y:r.y, w:r.w, h:r.h });
        }, 50);
    };

    const clamp = (c, r) => {
        const x = Math.max(r.x, Math.min(c.x, r.x+r.w-10));
        const y = Math.max(r.y, Math.min(c.y, r.y+r.h-10));
        const w = Math.max(10, Math.min(c.w, r.x+r.w-x));
        const h = Math.max(10, Math.min(c.h, r.y+r.h-y));
        return {x,y,w,h};
    };

    const getPos = (e) => {
        const con = containerRef.current; if (!con) return {x:0,y:0};
        const r = con.getBoundingClientRect(), src = e.touches?e.touches[0]:e;
        return { x: src.clientX-r.left, y: src.clientY-r.top };
    };

    const onDown = (e) => { e.preventDefault(); const r=calcImgRect()||imgRect; const p=getPos(e); setDrag({sx:p.x,sy:p.y,r}); setCrop({x:p.x,y:p.y,w:0,h:0}); };
    const onMove = (e) => {
        if (!drag) return; e.preventDefault();
        const p=getPos(e), nw=p.x-drag.sx, nh=p.y-drag.sy;
        setCrop(clamp({x:nw<0?p.x:drag.sx, y:nh<0?p.y:drag.sy, w:Math.abs(nw), h:Math.abs(nh)}, drag.r));
    };
    const onUp = () => setDrag(null);

    const applyCrop = () => {
        const img = imageRef.current; if (!img) return;
        if (crop.w < 5 || crop.h < 5) { alert('رسم منطقة الاقتصاص أولاً'); return; }
        const r = imgRect;
        const sx=(crop.x-r.x)*(img.naturalWidth/r.w), sy=(crop.y-r.y)*(img.naturalHeight/r.h);
        const sw=crop.w*(img.naturalWidth/r.w), sh=crop.h*(img.naturalHeight/r.h);
        const canvas = document.createElement('canvas');
        canvas.width=Math.round(sw); canvas.height=Math.round(sh);
        const ctx = canvas.getContext('2d');
        const di = new Image(); di.crossOrigin='anonymous';
        di.onload = () => {
            ctx.drawImage(di, Math.round(sx),Math.round(sy),Math.round(sw),Math.round(sh), 0,0,canvas.width,canvas.height);
            canvas.toBlob(blob => { if(blob){ onCropComplete(new File([blob],'cropped.jpg',{type:'image/jpeg'})); onClose(); } }, 'image/jpeg', 0.93);
        };
        di.onerror = () => alert('تعذّر اقتصاص الصورة');
        di.src = blobUrl || imageSrc;
    };

    const resetCrop = () => { if(imgRect.w>0) setCrop({x:imgRect.x,y:imgRect.y,w:imgRect.w,h:imgRect.h}); };

    if (!isOpen) return null;
    const showOverlay = imgLoaded && crop.w>2 && crop.h>2;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-[9999] flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white rounded-2xl shadow-2xl flex flex-col" style={{width:'min(720px,96vw)', maxHeight:'94vh'}}>
                <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-2xl flex-shrink-0">
                    <h3 className="font-bold text-lg">✂️ {title}</h3>
                    <button onClick={onClose} className="hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition"><X size={20}/></button>
                </div>
                <p className="text-xs text-gray-500 text-center py-2 bg-gray-50 border-b flex-shrink-0">
                    اسحب على الصورة لتحديد منطقة الاقتصاص
                </p>
                <div className="flex-1 overflow-auto p-3 bg-gray-100">
                    {!blobUrl ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="text-center text-gray-400">
                                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2"/>
                                <p className="text-sm">تحميل الصورة...</p>
                            </div>
                        </div>
                    ) : (
                        <div ref={containerRef} className="relative select-none" style={{cursor:'crosshair',userSelect:'none'}}
                            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
                            onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}>
                            <img ref={imageRef} src={blobUrl} alt="للاقتصاص" crossOrigin="anonymous"
                                onLoad={handleImageLoad} draggable={false}
                                style={{display:'block', maxWidth:'100%', maxHeight:'68vh', margin:'0 auto', objectFit:'contain', pointerEvents:'none'}}/>
                            {showOverlay && (<>
                                <svg className="absolute inset-0 pointer-events-none" style={{width:'100%',height:'100%'}}>
                                    <defs><mask id="cropMaskU"><rect width="100%" height="100%" fill="white"/><rect x={crop.x} y={crop.y} width={crop.w} height={crop.h} fill="black"/></mask></defs>
                                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#cropMaskU)"/>
                                </svg>
                                <div className="absolute pointer-events-none" style={{
                                    left:crop.x, top:crop.y, width:crop.w, height:crop.h,
                                    border:'2px solid white', boxShadow:'0 0 0 1px rgba(99,102,241,1)'
                                }}>
                                    <div style={{position:'absolute',inset:0,backgroundImage:`linear-gradient(rgba(255,255,255,0.2) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.2) 1px,transparent 1px)`,backgroundSize:`${crop.w/3}px ${crop.h/3}px`}}/>
                                </div>
                                <div className="absolute bg-indigo-600 text-white text-xs px-2 py-0.5 rounded pointer-events-none"
                                    style={{left:crop.x, top:Math.max(0,crop.y-22)}}>
                                    {Math.round(crop.w)} × {Math.round(crop.h)}
                                </div>
                            </>)}
                        </div>
                    )}
                </div>
                <div className="flex gap-3 p-4 border-t bg-white rounded-b-2xl flex-shrink-0">
                    <button onClick={applyCrop} disabled={crop.w<5}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white py-2.5 rounded-xl font-medium transition text-sm">
                        ✂️ تطبيق الاقتصاص
                    </button>
                    <button onClick={resetCrop} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-xl transition text-sm">🔄 إعادة</button>
                    <button onClick={onClose} className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2.5 rounded-xl transition text-sm">إلغاء</button>
                </div>
            </div>
        </div>
    );
};

export const ImageViewerModal = ({ isOpen, onClose, imageUrl, imageTitle, formData }) => {
    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleDownload = async () => {
        try {
            let fileName;
            if (imageTitle === 'صورة الجواز') {
                fileName = `${formData.NameArabic}-جواز.jpg`;
            } else if (imageTitle === 'الصورة الشخصية') {
                fileName = `${formData.NameArabic}-شخصية.jpg`;
            } else if (imageTitle === 'صورة الرخصة') {
                fileName = `${formData.NameArabic}-رخصة.jpg`;
            } else if (imageTitle === 'صورة البطاقة') {
                fileName = `${formData.NameArabic}-بطاقة.jpg`;
            } else {
                fileName = `${formData.NameArabic}.jpg`;
            }

            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('خطأ في تحميل الصورة:', error);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={handleBackdropClick}
        >
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4">
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {imageTitle || 'عرض الصورة'}
                    </h3>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownload}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-colors"
                            title="تحميل الصورة"
                        >
                            <Download size={20} />
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-gray-50 hover:bg-gray-100 text-gray-600 p-2 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
                <div className="p-4">
                    <div className="relative w-full flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden" style={{maxHeight:'82vh'}}>
                        <img
                            src={imageUrl}
                            alt={imageTitle}
                            style={{maxWidth:'100%', maxHeight:'82vh', objectFit:'contain', display:'block'}}
                            onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                        />
                        <div style={{display:'none'}} className="w-full h-48 items-center justify-center text-gray-400 flex-col">
                            <span className="text-4xl mb-2">🖼️</span>
                            <p className="text-sm">تعذّر تحميل الصورة</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// نافذة إعدادات التحذيرات
export const SettingsModal = ({ isOpen, onClose, settings, onSave }) => {
    const [localSettings, setLocalSettings] = useState(settings);

    useEffect(() => {
        setLocalSettings(settings);
    }, [settings]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave(localSettings);
        onClose();
    };

    const updatePassportSetting = (duration, value) => {
        setLocalSettings(prev => ({
            ...prev,
            passportSettings: {
                ...prev.passportSettings,
                [duration]: { minMonths: parseInt(value) || 0 }
            }
        }));
    };

    const updateLicenseSetting = (duration, value) => {
        setLocalSettings(prev => ({
            ...prev,
            licenseSettings: {
                ...prev.licenseSettings,
                [duration]: { minMonths: parseInt(value) || 0 }
            }
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            إعدادات التحذيرات والإشعارات
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition duration-200"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-8">
                    {/* إعدادات عامة للتفعيل */}
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.76 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            التحكم العام في التحذيرات
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white p-4 rounded-lg border">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    تحذيرات انتهاء صلاحية الجوازات
                                </label>
                                <select
                                    value={localSettings.enablePassportWarnings}
                                    onChange={(e) => setLocalSettings(prev => ({
                                        ...prev,
                                        enablePassportWarnings: e.target.value === 'true'
                                    }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="true">مفعل ✅</option>
                                    <option value="false">معطل ❌</option>
                                </select>
                            </div>

                            <div className="bg-white p-4 rounded-lg border">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    تحذيرات انتهاء صلاحية الرخص
                                </label>
                                <select
                                    value={localSettings.enableLicenseWarnings}
                                    onChange={(e) => setLocalSettings(prev => ({
                                        ...prev,
                                        enableLicenseWarnings: e.target.value === 'true'
                                    }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="true">مفعل ✅</option>
                                    <option value="false">معطل ❌</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* إعدادات الجوازات */}
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                            <span>🛂</span>
                            إعدادات تحذيرات الجوازات
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {Object.entries(localSettings.passportSettings || {}).map(([duration, config]) => (
                                <div key={duration} className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                                    <h4 className="font-medium text-gray-900 mb-3 text-center bg-blue-100 py-2 px-3 rounded">
                                        {duration}
                                    </h4>
                                    
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-2">
                                            الحد الأدنى لصلاحية الجواز
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="1"
                                                max="36"
                                                value={config.minMonths}
                                                onChange={(e) => updatePassportSetting(duration, e.target.value)}
                                                className="flex-1 border border-gray-300 rounded px-3 py-2 text-center font-semibold focus:ring-2 focus:ring-blue-500"
                                            />
                                            <span className="text-xs text-gray-600">شهر</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* إعدادات الرخص */}
                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                        <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                            <span>🚗</span>
                            إعدادات تحذيرات رخص القيادة
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {Object.entries(localSettings.licenseSettings || {}).map(([duration, config]) => (
                                <div key={duration} className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
                                    <h4 className="font-medium text-gray-900 mb-3 text-center bg-green-100 py-2 px-3 rounded">
                                        {duration}
                                    </h4>
                                    
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-2">
                                            الحد الأدنى لصلاحية الرخصة
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="1"
                                                max="36"
                                                value={config.minMonths}
                                                onChange={(e) => updateLicenseSetting(duration, e.target.value)}
                                                className="flex-1 border border-gray-300 rounded px-3 py-2 text-center font-semibold focus:ring-2 focus:ring-green-500"
                                            />
                                            <span className="text-xs text-gray-600">شهر</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* مثال توضيحي */}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <span>💡</span> مثال توضيحي
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="bg-white p-3 rounded border">
                                <h4 className="font-medium text-blue-600 mb-2">🛂 تحذيرات الجوازات:</h4>
                                <p className="text-gray-600">
                                    إذا كان شخص مسافر لمدة "3 أشهر" وجوازه ينتهي خلال {localSettings.passportSettings?.['3 أشهر']?.minMonths || 3} أشهر، 
                                    ستظهر رسالة تحذير.
                                </p>
                            </div>
                            <div className="bg-white p-3 rounded border">
                                <h4 className="font-medium text-green-600 mb-2">🚗 تحذيرات الرخص:</h4>
                                <p className="text-gray-600">
                                    إذا كان شخص مسافر لمدة "3 أشهر" ورخصته تنتهي خلال {localSettings.licenseSettings?.['3 أشهر']?.minMonths || 1} شهر، 
                                    ستظهر رسالة تحذير.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* أزرار التحكم */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                            onClick={() => {
                                setLocalSettings({
                                    enablePassportWarnings: true,
                                    enableLicenseWarnings: true,
                                    passportSettings: {
                                        '3 أشهر': { minMonths: 3 },
                                        '4 أشهر': { minMonths: 4 },
                                        '6 أشهر': { minMonths: 6 },
                                        'عقد': { minMonths: 12 },
                                        'تعقيب': { minMonths: 6 }
                                    },
                                    licenseSettings: {
                                        '3 أشهر': { minMonths: 1 },
                                        '4 أشهر': { minMonths: 2 },
                                        '6 أشهر': { minMonths: 3 },
                                        'عقد': { minMonths: 6 },
                                        'تعقيب': { minMonths: 2 }
                                    }
                                });
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg transition duration-200"
                        >
                            إعادة تعيين افتراضي
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg transition duration-200"
                        >
                            إلغاء
                        </button>
                        <button
                            onClick={handleSave}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg flex items-center gap-2 transition duration-200 shadow-lg"
                        >
                            <Save size={20} />
                            حفظ الإعدادات
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// نافذة الإعدادات الافتراضية
export const DefaultSettingsModal = ({ 
    isOpen, 
    onClose, 
    defaultSettings, 
    setDefaultSettings, 
    companies, 
    professions, 
    delegates, 
    travelDurations, 
    passportStatuses, 
    abilityTests, 
    militaryStatuses, 
    licenseTypes,
    onSave 
}) => {
    if (!isOpen) return null;

    const defaultEnabled = defaultSettings.enabled || {};
    const defaultValues = defaultSettings.values || {};
    const passportProfessionMode = defaultValues.PassportProfessionInputMode === 'text' || defaultEnabled.PassportProfessionText ? 'text' : 'select';

    const setPassportProfessionMode = (mode) => {
        const nextMode = mode === 'text' ? 'text' : 'select';
        setDefaultSettings(prev => ({
            ...prev,
            enabled: {
                ...prev.enabled,
                PassportProfessionID: nextMode === 'select' ? !!prev.enabled?.PassportProfessionID : false,
                PassportProfessionText: nextMode === 'text' ? !!prev.enabled?.PassportProfessionText : false
            },
            values: {
                ...prev.values,
                PassportProfessionInputMode: nextMode,
                PassportProfessionID: nextMode === 'select' ? (prev.values?.PassportProfessionID || '') : '',
                PassportProfessionText: nextMode === 'text' ? (prev.values?.PassportProfessionText || '') : ''
            }
        }));
    };

    const togglePassportProfessionDefault = (checked) => {
        setDefaultSettings(prev => {
            const mode = (prev.values?.PassportProfessionInputMode === 'text' || prev.enabled?.PassportProfessionText) ? 'text' : 'select';
            return {
                ...prev,
                enabled: {
                    ...prev.enabled,
                    PassportProfessionID: mode === 'select' ? checked : false,
                    PassportProfessionText: mode === 'text' ? checked : false
                },
                values: {
                    ...prev.values,
                    PassportProfessionInputMode: mode,
                    PassportProfessionID: mode === 'select' ? (prev.values?.PassportProfessionID || '') : '',
                    PassportProfessionText: mode === 'text' ? (prev.values?.PassportProfessionText || '') : ''
                }
            };
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-purple-900 flex items-center gap-2">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                            </svg>
                            ضبط النموذج الافتراضي
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition duration-200"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 كيف يعمل النظام:</h3>
                        <ul className="text-blue-700 text-sm space-y-1">
                            <li>• فعّل الحقول التي تريد ملؤها تلقائياً</li>
                            <li>• اختر القيم الافتراضية لكل حقل</li>
                            <li>• عند إضافة جواز جديد، ستُملأ الحقول المفعّلة تلقائياً</li>
                            <li>• يمكنك تعديل أي قيمة بعد التطبيق التلقائي</li>
                        </ul>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* الشركة */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.CompanyID}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, CompanyID: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">الشركة الافتراضية</label>
                            </div>
                            <select
                                value={defaultSettings.values.CompanyID}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, CompanyID: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.CompanyID}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.CompanyID 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر الشركة</option>
                                {companies.map(company => (
                                    <option key={company.CompanyID} value={company.CompanyID}>
                                        {company.CompanyName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* مدة السفر */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.TravelDuration}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, TravelDuration: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">مدة السفر الافتراضية</label>
                            </div>
                            <select
                                value={defaultSettings.values.TravelDuration}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, TravelDuration: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.TravelDuration}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.TravelDuration 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر المدة</option>
                                {travelDurations.map(duration => (
                                    <option key={duration} value={duration}>{duration}</option>
                                ))}
                            </select>
                        </div>

                        {/* حالة الجواز */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.PassportStatus}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, PassportStatus: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">حالة الجواز الافتراضية</label>
                            </div>
                            <select
                                value={defaultSettings.values.PassportStatus}
                                onChange={(e) => {
                                    const newStatus = e.target.value;
                                    setDefaultSettings(prev => ({
                                        ...prev,
                                        values: { 
                                            ...prev.values, 
                                            PassportStatus: newStatus,
                                            PreviousCompanies: newStatus === 'جديد' ? [] : prev.values.PreviousCompanies
                                        },
                                        enabled: {
                                            ...prev.enabled,
                                            PreviousCompanies: newStatus === 'قديم شركات' ? prev.enabled.PreviousCompanies : false
                                        }
                                    }));
                                }}
                                disabled={!defaultSettings.enabled.PassportStatus}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.PassportStatus 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر الحالة</option>
                                {passportStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
 
                        {/* الشركات السابقة */}
                        {defaultSettings.values.PassportStatus === 'قديم شركات' && (
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center gap-3 mb-3">
                                    <input
                                        type="checkbox"
                                        checked={defaultSettings.enabled.PreviousCompanies || false}
                                        onChange={(e) => setDefaultSettings(prev => ({
                                            ...prev,
                                            enabled: { ...prev.enabled, PreviousCompanies: e.target.checked }
                                        }))}
                                        className="w-5 h-5 text-purple-600"
                                    />
                                    <label className="font-medium text-gray-700">الشركات السابقة الافتراضية</label>
                                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">
                                        فقط للجوازات القديمة
                                    </span>
                                </div>
                                
                                {Array.isArray(defaultSettings.values.PreviousCompanies) && defaultSettings.values.PreviousCompanies.length > 0 && (
                                    <div className="mb-3 flex flex-wrap gap-1">
                                        {defaultSettings.values.PreviousCompanies.map((companyId) => {
                                            const company = companies.find(c => c.CompanyID.toString() === companyId?.toString());
                                            if (!company) return null;
                                            return (
                                                <span 
                                                    key={companyId} 
                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700"
                                                >
                                                    {company.CompanyName}
                                                    {defaultSettings.enabled.PreviousCompanies && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setDefaultSettings(prev => ({
                                                                ...prev,
                                                                values: {
                                                                    ...prev.values,
                                                                    PreviousCompanies: (prev.values.PreviousCompanies || []).filter(id => id !== companyId)
                                                                }
                                                            }))}
                                                            className="ml-1 hover:text-red-600 focus:outline-none"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                <select
                                    onChange={(e) => {
                                        const selectedCompanyId = e.target.value;
                                        if (selectedCompanyId && !(defaultSettings.values.PreviousCompanies || []).includes(selectedCompanyId)) {
                                            setDefaultSettings(prev => ({
                                                ...prev,
                                                values: {
                                                    ...prev.values,
                                                    PreviousCompanies: [...(prev.values.PreviousCompanies || []), selectedCompanyId]
                                                }
                                            }));
                                        }
                                        e.target.value = '';
                                    }}
                                    disabled={!defaultSettings.enabled.PreviousCompanies}
                                    className={`w-full border rounded-lg px-3 py-2 ${
                                        defaultSettings.enabled.PreviousCompanies 
                                            ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                            : 'bg-gray-200 border-gray-300'
                                    }`}
                                >
                                    <option value="">إضافة شركة سابقة...</option>
                                    {companies
                                        .filter(company => !(defaultSettings.values.PreviousCompanies || []).includes(company.CompanyID.toString()))
                                        .map(company => (
                                            <option key={company.CompanyID} value={company.CompanyID}>
                                                {company.CompanyName}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                        )}
 
                        {/* رسالة توضيحية للجواز الجديد */}
                        {defaultSettings.values.PassportStatus === 'جديد' && (
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 text-blue-700">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">معلومة مهمة</span>
                                </div>
                                <p className="text-sm text-blue-600 mt-2">
                                    الجوازات الجديدة لا تحتاج لشركات سابقة. خيار "الشركات السابقة" متاح فقط للجوازات "قديم شركات".
                                </p>
                            </div>
                        )}
 
                        {/* نتيجة اختبار القدرات */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.AbilityTest}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, AbilityTest: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">نتيجة اختبار القدرات</label>
                            </div>
                            <select
                                value={defaultSettings.values.AbilityTest}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, AbilityTest: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.AbilityTest}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.AbilityTest 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر النتيجة</option>
                                {abilityTests.map(test => (
                                    <option key={test} value={test}>{test}</option>
                                ))}
                            </select>
                        </div>
 
                        {/* طريقة المهنة بالجواز */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 md:col-span-2">
                            <h3 className="font-bold text-blue-900 mb-3">طريقة حقل المهنة بالجواز</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setPassportProfessionMode('select')}
                                    className={`text-right p-4 rounded-xl border transition ${passportProfessionMode === 'select' ? 'bg-white border-blue-500 ring-2 ring-blue-100' : 'bg-blue-100/50 border-blue-200 hover:bg-white'}`}
                                >
                                    <div className="font-black text-gray-900">اختيار من قائمة فقط</div>
                                    <div className="text-xs text-gray-600 mt-1">بحث واختيار من جدول المهن فقط، بدون كتابة نص حر.</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPassportProfessionMode('text')}
                                    className={`text-right p-4 rounded-xl border transition ${passportProfessionMode === 'text' ? 'bg-white border-blue-500 ring-2 ring-blue-100' : 'bg-blue-100/50 border-blue-200 hover:bg-white'}`}
                                >
                                    <div className="font-black text-gray-900">كتابة نصية فقط</div>
                                    <div className="text-xs text-gray-600 mt-1">تحفظ داخل الجواز فقط ولا تُضاف إلى قائمة المهن.</div>
                                </button>
                            </div>
                        </div>

                        {/* المهنة بالجواز */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={passportProfessionMode === 'text' ? !!defaultSettings.enabled.PassportProfessionText : !!defaultSettings.enabled.PassportProfessionID}
                                    onChange={(e) => togglePassportProfessionDefault(e.target.checked)}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">المهنة بالجواز</label>
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                    {passportProfessionMode === 'text' ? 'نصي فقط' : 'اختيار فقط'}
                                </span>
                            </div>
                            {passportProfessionMode === 'select' ? (
                                <select
                                    value={defaultSettings.values.PassportProfessionID || ''}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, PassportProfessionID: true, PassportProfessionText: false },
                                        values: { ...prev.values, PassportProfessionInputMode: 'select', PassportProfessionID: e.target.value, PassportProfessionText: '' }
                                    }))}
                                    disabled={!defaultSettings.enabled.PassportProfessionID}
                                    className={`w-full border rounded-lg px-3 py-2 ${
                                        defaultSettings.enabled.PassportProfessionID 
                                            ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                            : 'bg-gray-200 border-gray-300'
                                    }`}
                                >
                                    <option value="">اختر المهنة</option>
                                    {professions.map(profession => (
                                        <option key={profession.ProfessionID} value={profession.ProfessionID}>
                                            {profession.ProfessionName}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={defaultSettings.values.PassportProfessionText || ''}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, PassportProfessionID: false, PassportProfessionText: true },
                                        values: { ...prev.values, PassportProfessionInputMode: 'text', PassportProfessionID: '', PassportProfessionText: e.target.value }
                                    }))}
                                    disabled={!defaultSettings.enabled.PassportProfessionText}
                                    className={`w-full border rounded-lg px-3 py-2 ${
                                        defaultSettings.enabled.PassportProfessionText
                                            ? 'border-purple-300 focus:ring-2 focus:ring-purple-500 bg-white'
                                            : 'bg-gray-200 border-gray-300'
                                    }`}
                                    placeholder="اكتب المهنة بالجواز نصيًا"
                                    dir="rtl"
                                    maxLength={255}
                                />
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                                النظام يمنع تشغيل النص والقائمة معًا. المهنة النصية لا تظهر لاحقًا في قائمة الاختيارات.
                            </p>
                        </div>
 
                        {/* مهنة السفر */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.TravelProfessionID}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, TravelProfessionID: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">مهنة السفر</label>
                            </div>
                            <select
                                value={defaultSettings.values.TravelProfessionID}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, TravelProfessionID: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.TravelProfessionID}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.TravelProfessionID 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر المهنة</option>
                                {professions.map(profession => (
                                    <option key={profession.ProfessionID} value={profession.ProfessionID}>
                                        {profession.ProfessionName}
                                    </option>
                                ))}
                            </select>
                        </div>
 
                        {/* الموقف من التجنيد */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.MilitaryStatus}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, MilitaryStatus: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">الموقف من التجنيد</label>
                            </div>
                            <select
                                value={defaultSettings.values.MilitaryStatus}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, MilitaryStatus: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.MilitaryStatus}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.MilitaryStatus 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر الموقف</option>
                                {militaryStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
 
                        {/* المندوب */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.DelegateID}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, DelegateID: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">المندوب الافتراضي</label>
                            </div>
                            <select
                                value={defaultSettings.values.DelegateID}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, DelegateID: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.DelegateID}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.DelegateID 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر المندوب</option>
                                {delegates.map(delegate => (
                                    <option key={delegate.DelegateID} value={delegate.DelegateID}>
                                        {delegate.DelegateName}
                                    </option>
                                ))}
                            </select>
                        </div>
 
                        {/* نوع الرخصة */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={defaultSettings.enabled.LicenseType}
                                    onChange={(e) => setDefaultSettings(prev => ({
                                        ...prev,
                                        enabled: { ...prev.enabled, LicenseType: e.target.checked }
                                    }))}
                                    className="w-5 h-5 text-purple-600"
                                />
                                <label className="font-medium text-gray-700">نوع الرخصة</label>
                            </div>
                            <select
                                value={defaultSettings.values.LicenseType}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    values: { ...prev.values, LicenseType: e.target.value }
                                }))}
                                disabled={!defaultSettings.enabled.LicenseType}
                                className={`w-full border rounded-lg px-3 py-2 ${
                                    defaultSettings.enabled.LicenseType 
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-500' 
                                        : 'bg-gray-200 border-gray-300'
                                }`}
                            >
                                <option value="">اختر نوع الرخصة</option>
                                {licenseTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* الملاحظة الافتراضية - خارج الـ grid عرض كامل */}
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mt-6">
                        <div className="flex items-center gap-3 mb-3">
                            <input
                                type="checkbox"
                                checked={defaultSettings.enabled.DefaultNote || false}
                                onChange={(e) => setDefaultSettings(prev => ({
                                    ...prev,
                                    enabled: { ...prev.enabled, DefaultNote: e.target.checked }
                                }))}
                                className="w-5 h-5 text-purple-600"
                            />
                            <label className="font-medium text-gray-700 flex items-center gap-2">
                                <span>📝</span>
                                ملاحظة افتراضية تُضاف تلقائياً لكل جواز جديد
                            </label>
                        </div>
                        <textarea
                            value={defaultSettings.values.DefaultNote || ''}
                            onChange={(e) => setDefaultSettings(prev => ({
                                ...prev,
                                values: { ...prev.values, DefaultNote: e.target.value }
                            }))}
                            disabled={!defaultSettings.enabled.DefaultNote}
                            rows={3}
                            placeholder="اكتب الملاحظة الافتراضية هنا..."
                            dir="rtl"
                            className={`w-full border rounded-lg px-3 py-2 resize-none text-sm ${
                                defaultSettings.enabled.DefaultNote
                                    ? 'border-yellow-400 focus:ring-2 focus:ring-yellow-500 bg-white'
                                    : 'bg-gray-200 border-gray-300 text-gray-400'
                            }`}
                        />
                        {defaultSettings.enabled.DefaultNote && defaultSettings.values.DefaultNote?.trim() && (
                            <p className="text-xs text-yellow-700 mt-2 flex items-center gap-1">
                                <span>✅</span>
                                ستُضاف هذه الملاحظة تلقائياً عند فتح نموذج جواز جديد
                            </p>
                        )}
                    </div>
 
                    {/* أزرار التحكم */}
                    <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
                        <button
                            onClick={() => {
                                setDefaultSettings({
                                    enabled: {
                                        CompanyID: false,
                                        TravelDuration: false,
                                        PassportStatus: false,
                                        PreviousCompanies: false,
                                        AbilityTest: false,
                                        PassportProfessionID: false,
                                        PassportProfessionText: false,
                                        TravelProfessionID: false,
                                        MilitaryStatus: false,
                                        DelegateID: false,
                                        LicenseType: false,
                                        MedicalExamStatus: false,
                                        DefaultNote: false
                                    },
                                    values: {
                                        CompanyID: '',
                                        TravelDuration: '',
                                        PassportStatus: '',
                                        PreviousCompanies: [],
                                        AbilityTest: '',
                                        PassportProfessionID: '',
                                        PassportProfessionText: '',
                                        PassportProfessionInputMode: 'select',
                                        TravelProfessionID: '',
                                        MilitaryStatus: '',
                                        DelegateID: '',
                                        LicenseType: '',
                                        MedicalExamStatus: '',
                                        DefaultNote: ''
                                    }
                                });
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg transition duration-200"
                        >
                            إعادة تعيين
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg transition duration-200"
                        >
                            إلغاء
                        </button>
                        <button
                            onClick={() => {
                                onSave(defaultSettings);
                                onClose();
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg flex items-center gap-2 transition duration-200 shadow-lg"
                        >
                            <Save size={20} />
                            حفظ الإعدادات
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
 };
 
 // نافذة المعالجة المجمعة
 export const BatchUploadModal = ({ 
    isOpen, 
    onClose, 
    batchImages, 
    setBatchImages, 
    batchProcessing, 
    batchProgress, 
    batchResults, 
    currentBatchIndex,
    onUpload,
    onStartProcessing,
    onViewPassport 
 }) => {
    if (!isOpen) return null;
 
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[300] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
                {/* رأس النافذة */}
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white p-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold">🚀 المعالجة المجمعة للجوازات</h2>
                            <p className="text-purple-100 mt-1">رفع ومعالجة عدة جوازات دفعة واحدة</p>
                        </div>
                            <button
            onClick={() => window.open('/settings/api-keys', '_blank')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition duration-200 text-sm"
            title="فتح إعدادات OCR في تاب جديد"
            disabled={batchProcessing}
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span>إعدادات OCR</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
        </button>
                        <button
                            onClick={() => {
                                if (!batchProcessing) {
                                    onClose();
                                }
                            }}
                            disabled={batchProcessing}
                            className={`${batchProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-200'} transition-colors`}
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>
 
                <div className="p-6">
                    {/* منطقة رفع الصور */}
                    {batchImages.length === 0 && (
                        <div className="border-2 border-dashed border-purple-300 rounded-lg p-12 text-center bg-purple-50">
                            <label className="cursor-pointer block">
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => onUpload(e.target.files)}
                                    className="hidden"
                                    disabled={batchProcessing}
                                />
                                <div className="text-center">
                                    <svg className="w-16 h-16 mx-auto text-purple-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <h3 className="text-xl font-bold text-purple-600 mb-2">اختر صور الجوازات</h3>
                                    <p className="text-purple-500 mb-4">يمكنك اختيار حتى 50 صورة جواز للمعالجة المجمعة</p>
                                    <div className="bg-purple-600 text-white px-6 py-3 rounded-lg inline-block hover:bg-purple-700 transition font-bold">
                                        📁 اختيار الصور
                                    </div>
                                </div>
                            </label>
                        </div>
                    )}
 
                    {/* قائمة الصور المرفوعة */}
                    {batchImages.length > 0 && (
                        <div>
                            {/* شريط التحكم */}
                            <div className="flex justify-between items-center mb-6 p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-4">
                                    <span className="font-bold text-gray-700">
                                        📊 إجمالي: {batchImages.length} صورة
                                    </span>
                                    <span className="text-green-600 font-medium">
                                        ✅ مكتمل: {batchImages.filter(img => img.status === 'completed').length}
                                    </span>
                                    <span className="text-red-600 font-medium">
                                        ❌ فشل: {batchImages.filter(img => img.status === 'error').length}
                                    </span>
                                </div>
                                
                                <div className="flex gap-3">
                                    {!batchProcessing && (
                                        <>
                                            <label className="bg-blue-100 hover:bg-blue-200 text-blue-600 px-4 py-2 rounded-lg cursor-pointer transition">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={(e) => onUpload(e.target.files)}
                                                    className="hidden"
                                                />
                                                + إضافة المزيد
                                            </label>
                                            
                                            <button
                                                onClick={() => setBatchImages([])}
                                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-lg transition"
                                            >
                                                🗑️ مسح الكل
                                            </button>
                                            
                                            <button
                                                onClick={onStartProcessing}
                                                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-6 py-2 rounded-lg font-bold transition shadow-lg"
                                            >
                                                🚀 بدء المعالجة
                                            </button>
                                        </>
                                    )}
                                    
                                    {batchProcessing && (
                                        <div className="bg-yellow-100 text-yellow-700 px-4 py-2 rounded-lg font-medium">
                                            ⏳ جاري المعالجة... {Math.round(batchProgress)}%
                                        </div>
                                    )}
                                </div>
                            </div>
 
                            {/* شريط التقدم العام */}
                            {batchProcessing && (
                                <div className="mb-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            معالجة الصورة {currentBatchIndex + 1} من {batchImages.length}
                                        </span>
                                        <span className="text-sm text-gray-500">{Math.round(batchProgress)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div 
                                            className="bg-gradient-to-r from-green-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                                            style={{ width: `${batchProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
 
                            {/* جدول الصور */}
                            <div className="max-h-96 overflow-y-auto border rounded-lg">
                                <table className="w-full">
                                    <thead className="bg-gray-100 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 text-right">#</th>
                                            <th className="px-4 py-3 text-right">اسم الملف</th>
                                            <th className="px-4 py-3 text-center">الحالة</th>
                                            <th className="px-4 py-3 text-center">البيانات المستخرجة</th>
                                            <th className="px-4 py-3 text-center">رقم الجواز</th>
                                            <th className="px-4 py-3 text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batchImages.map((image, index) => (
                                            <tr key={image.id} className={`border-b ${
                                                image.status === 'processing' ? 'bg-blue-50' : 
                                                image.status === 'completed' ? 'bg-green-50' : 
                                                image.status === 'error' ? 'bg-red-50' : ''
                                            }`}>
                                                <td className="px-4 py-3 font-bold">{index + 1}</td>
                                                <td className="px-4 py-3">
                                                    <div className="font-medium">{image.name}</div>
                                                    <div className="text-sm text-gray-500">{(image.file.size / 1024 / 1024).toFixed(2)} MB</div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {image.status === 'waiting' && <span className="text-gray-500">⏳ في الانتظار</span>}
                                                    {image.status === 'processing' && <span className="text-blue-600">🔄 جاري المعالجة</span>}
                                                    {image.status === 'completed' && <span className="text-green-600">✅ مكتمل</span>}
                                                    {image.status === 'error' && <span className="text-red-600">❌ فشل</span>}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {image.extractedData && (
                                                        <div className="text-sm">
                                                            <div>📝 {Object.keys(image.extractedData).filter(k => image.extractedData[k]).length} حقل</div>
                                                            {image.extractedData.NameArabic && (
                                                                <div className="text-blue-600">{image.extractedData.NameArabic}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {image.error && (
                                                        <div className="text-red-600 text-sm">
                                                            {image.error}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {image.passportId && (
                                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-bold">
                                                            {image.passportId}
                                                            </span>
                                                   )}
                                               </td>
                                               <td className="px-4 py-3 text-center">
                                                   {image.status === 'completed' && image.passportId && (
                                                       <button
                                                           onClick={() => onViewPassport(image.passportId)}
                                                           className="bg-blue-100 hover:bg-blue-200 text-blue-600 px-3 py-1 rounded text-sm transition"
                                                       >
                                                           📝 مراجعة
                                                       </button>
                                                   )}
                                                   {!batchProcessing && image.status === 'waiting' && (
                                                       <button
                                                           onClick={() => {
                                                               setBatchImages(prev => prev.filter(img => img.id !== image.id));
                                                           }}
                                                           className="bg-red-100 hover:bg-red-200 text-red-600 px-3 py-1 rounded text-sm transition"
                                                       >
                                                           🗑️ حذف
                                                       </button>
                                                   )}
                                               </td>
                                           </tr>
                                       ))}
                                   </tbody>
                               </table>
                           </div>

                           {/* ملخص النتائج */}
                           {batchResults.length > 0 && !batchProcessing && (
                               <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                   <h3 className="font-bold text-blue-800 mb-3">📊 ملخص النتائج</h3>
                                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                       <div className="bg-green-100 p-3 rounded text-center">
                                           <div className="text-2xl font-bold text-green-600">
                                               {batchResults.filter(r => r.success).length}
                                           </div>
                                           <div className="text-green-700">جواز نجح</div>
                                       </div>
                                       <div className="bg-red-100 p-3 rounded text-center">
                                           <div className="text-2xl font-bold text-red-600">
                                               {batchResults.filter(r => !r.success).length}
                                           </div>
                                           <div className="text-red-700">جواز فشل</div>
                                       </div>
                                       <div className="bg-blue-100 p-3 rounded text-center">
                                           <div className="text-2xl font-bold text-blue-600">
                                               {Math.round((batchResults.filter(r => r.success).length / batchResults.length) * 100)}%
                                           </div>
                                           <div className="text-blue-700">معدل النجاح</div>
                                       </div>
                                   </div>
                               </div>
                           )}
                       </div>
                   )}
               </div>
           </div>
       </div>
   );
};

// نافذة الإدخال السريع
export const QuickEntryModal = ({ 
   isOpen, 
   onClose, 
   quickEntryStep, 
   setQuickEntryStep,
   quickEntryData, 
   setQuickEntryData,
   quickEntryError, 
   setQuickEntryError,
   searchTerm: _searchTermProp, 
   setSearchTerm: _setSearchTermProp,
   isLoading,
   companies,
   professions,
   delegates,
   travelDurations,
   passportStatuses,
   abilityTests,
   militaryStatuses,
   licenseTypes,
   defaultSettings,
   onSubmit,
   getVisibleQuickEntryFields: getVisibleQuickEntryFieldsFromParent,
   onReset,
   onValidateField,
   onHandleInput,
   onHandleKeyDown,
   onGoToNext,
   onGoToPrevious
}) => {
   // searchTerm محلي داخل المكوّن لمنع مسحه عند re-render الأب
   const [searchTerm, setSearchTerm] = useState('');
   // counter لإجبار re-render عند تغيير التثبيت
   const [pinnedVersion, setPinnedVersion] = useState(0);
   // ref للحقل عشان نتحكم فيه مباشرة بدون re-render
   const searchInputRef = React.useRef(null);
   const searchValueRef = React.useRef('');
   const phoneInputRef = React.useRef(null);

   // مسح البحث عند تغيير الخطوة
   React.useEffect(() => {
       searchValueRef.current = '';
       setSearchTerm('');
       if (searchInputRef.current) {
           searchInputRef.current.value = '';
           setTimeout(() => { if (searchInputRef.current) searchInputRef.current.focus(); }, 50);
       }
   }, [quickEntryStep]);

   if (!isOpen) return null;

   const safeDefaultSettings = defaultSettings || { enabled: {}, values: {} };
   const passportProfessionMode = safeDefaultSettings.values?.PassportProfessionInputMode === 'text' || safeDefaultSettings.enabled?.PassportProfessionText
       ? 'text'
       : 'select';

   // فلترة الحقول المرئية
   const quickEntryFields = [
       {
           key: 'CompanyID',
           title: 'الشركة',
           type: 'select',
           required: true,
           options: () => companies.map((company, index) => ({
               value: company.CompanyID,
               label: `${index + 1}. ${company.CompanyName}`
           }))
       },
       {
           key: 'TravelDuration',
           title: 'مدة السفر',
           type: 'select',
           required: true,
           options: () => travelDurations.map((duration, index) => ({
               value: duration,
               label: `${index + 1}. ${duration}`
           }))
       },
       {
           key: 'PassportStatus',
           title: 'حالة الجواز',
           type: 'select',
           required: true,
           options: () => passportStatuses.map((status, index) => ({
               value: status,
               label: `${index + 1}. ${status}`
           }))
       },
       {
           key: 'PreviousCompanies',
           title: 'الشركات السابقة',
           type: 'multiselect',
           required: false,
           condition: (data) => data.PassportStatus === 'قديم شركات',
           options: () => companies.map((company, index) => ({
               value: company.CompanyID.toString(),
               label: `${index + 1}. ${company.CompanyName}`
           }))
       },
       {
           key: 'AbilityTest',
           title: 'نتيجة اختبار القدرات',
           type: 'select',
           required: true,
           options: () => abilityTests.map((test, index) => ({
               value: test,
               label: `${index + 1}. ${test}`
           }))
       },
       {
           key: 'NameArabic',
           title: 'الاسم بالعربي',
           type: 'text',
           required: true,
           placeholder: 'أدخل الاسم الكامل بالعربي',
           validation: (value) => {
               if (!value) return 'الاسم مطلوب';
               if (!/^[\u0600-\u06FF\s]+$/.test(value)) return 'يجب استخدام العربية فقط';
               return null;
           }
       },
       {
           key: 'NameEnglish',
           title: 'الاسم بالإنجليزي',
           type: 'text',
           required: true,
           placeholder: 'Enter full name in English',
           validation: (value) => {
               if (!value) return 'Name is required';
               if (!/^[A-Za-z\s]+$/.test(value)) return 'English letters only';
               return null;
           }
       },
       {
           key: 'PassportNumber',
           title: 'رقم الجواز',
           type: 'text',
           required: true,
           placeholder: 'A12345678',
           validation: (value) => {
               if (!value) return 'رقم الجواز مطلوب';
               if (!/^A[0-9]{8}$/.test(value)) return 'يجب أن يبدأ بـ A و8 أرقام';
               return null;
           }
       },
       {
           key: 'NationalID',
           title: 'الرقم القومي',
           type: 'text',
           required: true,
           placeholder: '12345678901234',
           validation: (value) => {
               if (!value) return 'الرقم القومي مطلوب';
               if (!/^[0-9]{14}$/.test(value)) return 'يجب أن يكون 14 رقم';
               return null;
           }
       },
       {
           key: 'PassportProfessionID',
           title: 'المهنة بالجواز',
           type: 'select',
           required: true,
           condition: () => passportProfessionMode === 'select',
           placeholder: 'ابحث أو اكتب رقم الاختيار',
           helpText: 'وضع مهنة الجواز مضبوط على الاختيار من القائمة فقط.',
           options: () => professions.map((profession, index) => ({
               value: profession.ProfessionID,
               label: `${index + 1}. ${profession.ProfessionName}`,
               searchText: profession.ProfessionName
           }))
       },
       {
           key: 'PassportProfessionText',
           title: 'المهنة بالجواز',
           type: 'text',
           required: true,
           condition: () => passportProfessionMode === 'text',
           placeholder: 'اكتب المهنة بالجواز نصيًا',
           helpText: 'وضع مهنة الجواز مضبوط على الكتابة النصية فقط، ولن تُضاف إلى جدول المهن.',
           validation: (value) => {
               if (!value || !String(value).trim()) return 'المهنة بالجواز مطلوبة';
               return null;
           }
       },
       {
           key: 'TravelProfessionID',
           title: 'مهنة السفر',
           type: 'select',
           required: true,
           options: () => professions.map((profession, index) => ({
               value: profession.ProfessionID,
               label: `${index + 1}. ${profession.ProfessionName}`
           }))
       },
       {
           key: 'MilitaryStatus',
           title: 'الموقف من التجنيد',
           type: 'select',
           required: true,
           options: () => militaryStatuses.map((status, index) => ({
               value: status,
               label: `${index + 1}. ${status}`
           }))
       },
       {
           key: 'PassportExpiryDate',
           title: 'تاريخ انتهاء الجواز',
           type: 'date',
           required: true,
           placeholder: 'اختر التاريخ'
       },
       {
           key: 'Address',
           title: 'العنوان',
           type: 'text',
           required: true,
           placeholder: 'أدخل العنوان كاملاً',
           validation: (value) => {
               if (!value) return 'العنوان مطلوب';
               if (!/^[\u0600-\u06FF\s\d،,\/\-]+$/.test(value)) return 'يجب استخدام العربية فقط';
               return null;
           }
       },
       {
           key: 'DelegateID',
           title: 'المندوب',
           type: 'select',
           required: true,
           options: () => delegates.map((delegate, index) => ({
               value: delegate.DelegateID,
               label: `${index + 1}. ${delegate.DelegateName}`
           }))
       },
       {
        key: 'PhoneNumbers',
        title: 'أرقام الهواتف',
        type: 'phone',
        required: true,
        placeholder: '01xxxxxxxxx',
        validation: (value) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
                return 'يجب إدخال رقم هاتف واحد على الأقل';
            }
            
            const phonesToCheck = Array.isArray(value) ? value : [value];
            for (let phone of phonesToCheck) {
                // تحويل إلى string والتأكد من وجود قيمة
                const phoneStr = phone ? String(phone).trim() : '';
                if (!phoneStr || !/^01[0-9]{9}$/.test(phoneStr)) {
                    return `رقم غير صحيح: ${phoneStr || 'فارغ'}`;
                }
            }
            return null;
        }
    },
       {
           key: 'Images',
           title: 'رفع الصور',
           type: 'images',
           required: false,
           description: 'اختر الصور التي تريد رفعها (كلها اختيارية)'
       },
       {
           key: 'Notes',
           title: 'الملاحظات',
           type: 'textarea',
           required: false,
           placeholder: 'اكتب أي ملاحظات إضافية...'
       }
   ];

   const getVisibleQuickEntryFields = () => {
       if (typeof getVisibleQuickEntryFieldsFromParent === 'function') {
           try {
               const parentFields = getVisibleQuickEntryFieldsFromParent();
               if (Array.isArray(parentFields) && parentFields.length) return parentFields;
           } catch (error) {
               console.warn('تعذر استخدام حقول الإدخال السريع من الأب، سيتم استخدام الحقول المحلية:', error);
           }
       }

       return quickEntryFields.filter(field => {
           // تطبيق شرط الرؤية العادي
           if (field.condition && !field.condition(quickEntryData)) {
               return false;
           }
           
           const fieldEnabled = !!safeDefaultSettings.enabled?.[field.key];
           const fieldValue = safeDefaultSettings.values?.[field.key];

           // إخفاء الحقول التي لها قيم افتراضية مفعّلة
           if (fieldEnabled && fieldValue) {
               if (Array.isArray(fieldValue)) {
                   return fieldValue.length === 0;
               }
               return false;
           }
           
           return true;
       });
   };

   const visibleFields = getVisibleQuickEntryFields();
   const totalDefaultFields = Object.keys(safeDefaultSettings.enabled || {})
       .filter(key => safeDefaultSettings.enabled?.[key] && safeDefaultSettings.values?.[key]).length;
   
   const currentField = visibleFields[quickEntryStep];
   const progress = ((quickEntryStep + 1) / visibleFields.length) * 100;
   const isLargeField = currentField?.type === 'select' && 
       ['CompanyID', 'PassportProfessionID', 'TravelProfessionID', 'DelegateID'].includes(currentField.key);
   const isMultiSelect = currentField?.type === 'multiselect';

   // ===== نظام التثبيت =====
   // المفاتيح المدعومة للتثبيت
   const PIN_KEYS = ['CompanyID', 'PreviousCompanies', 'PassportProfessionID', 'TravelProfessionID', 'DelegateID'];
   
   const getPinnedItems = (fieldKey) => {
       try {
           const stored = localStorage.getItem(`pinned_${fieldKey}`);
           return stored ? JSON.parse(stored) : [];
       } catch { return []; }
   };
   
   const togglePin = (fieldKey, value) => {
       const pinned = getPinnedItems(fieldKey);
       let updated;
       if (pinned.includes(String(value))) {
           updated = pinned.filter(v => v !== String(value));
       } else {
           updated = [String(value), ...pinned];
       }
       localStorage.setItem(`pinned_${fieldKey}`, JSON.stringify(updated));
       // إجبار re-render لتحديث حالة التثبيت
       setPinnedVersion(v => v + 1);
   };
   
   const isPinned = (fieldKey, value) => {
       return getPinnedItems(fieldKey).includes(String(value));
   };
   
   // ترتيب الخيارات مع المثبتة في الأعلى
   const getSortedWithPinned = (fieldKey, options) => {
       const pinned = getPinnedItems(fieldKey);
       if (!pinned.length) return options;
       const pinnedItems = options.filter(o => pinned.includes(String(o.value)));
       const rest = options.filter(o => !pinned.includes(String(o.value)));
       return [...pinnedItems, ...rest];
   };

   // فلترة الخيارات بناءً على البحث (رقم أو نص)
   const getFilteredOptions = () => {
       if (!currentField?.options) return [];
       const options = currentField.options();
       // الترتيب الفعلي المعروض (مع التثبيت)
       const sortedOptions = PIN_KEYS.includes(currentField.key) 
           ? getSortedWithPinned(currentField.key, options) 
           : options;
       if (!searchTerm) {
           return sortedOptions;
       }
       const term = searchTerm.trim();
       // إذا كان رقماً صحيحاً، ابحث بالرقم بناءً على الترتيب الفعلي المعروض
       const asNumber = parseInt(term);
       if (!isNaN(asNumber) && String(asNumber) === term) {
           const byIndex = sortedOptions.filter((_, i) => i + 1 === asNumber);
           if (byIndex.length > 0) return byIndex;
       }
       // البحث بالنص (الاسم)
       const filtered = options.filter(option => {
           const haystack = `${option.label || ''} ${option.searchText || ''}`.toLowerCase();
           return haystack.includes(term.toLowerCase());
       });
       if (PIN_KEYS.includes(currentField.key)) {
           return getSortedWithPinned(currentField.key, filtered);
       }
       return filtered;
   };

   return (
       <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-1">
           <div className={`bg-white rounded-xl shadow-2xl overflow-hidden ${
               (isLargeField || isMultiSelect) 
                   ? 'w-full max-w-6xl max-h-[95vh]' 
                   : 'w-full max-w-2xl max-h-[80vh]'
           }`}>
               {/* رأس النافذة */}
               <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-3 rounded-t-xl">
                   <div className="flex justify-between items-center">
                       <div>
                           <h2 className="text-xl font-bold">⚡ الإدخال السريع</h2>
                           <p className="text-green-100 text-sm">
                               الحقل {quickEntryStep + 1} من {visibleFields.length}: {currentField?.title}
                           </p>
                           {totalDefaultFields > 0 && (
                               <div className="flex items-center gap-2 mt-1">
                                   <span className="text-xs bg-green-600 bg-opacity-50 px-2 py-1 rounded-full">
                                       ⚙️ {totalDefaultFields} حقل افتراضي مفعّل
                                   </span>
                                   <span className="text-xs text-green-200">
                                       (سيتم تطبيقها تلقائياً)
                                   </span>
                               </div>
                           )}
                       </div>
                       <button
                           onClick={onReset}
                           className="text-white hover:text-red-200 transition-colors"
                       >
                           <X size={20} />
                       </button>
                   </div>
                   
                   {/* شريط التقدم */}
                   <div className="mt-2">
                       <div className="w-full bg-white bg-opacity-20 rounded-full h-1.5">
                           <div 
                               className="bg-white h-1.5 rounded-full transition-all duration-300"
                               style={{ width: `${progress}%` }}
                           ></div>
                       </div>
                   </div>
               </div>

               {/* محتوى النافذة */}
               <div className="p-4 overflow-y-auto" style={{ 
                   maxHeight: (isLargeField || isMultiSelect) ? 'calc(95vh - 120px)' : 'calc(80vh - 120px)' 
               }}>
                   
                   {/* الحقول الكبيرة */}
                   {isLargeField && (
                       <div>
                           <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                               {currentField.title}
                               {currentField.required && <span className="text-red-500 mr-1">*</span>}
                           </h3>
                           {currentField.helpText && (
                               <p className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2 mb-4 text-center">{currentField.helpText}</p>
                           )}
                           
                           {/* حقل بحث موحد - رقم أو اسم */}
                           <div className="mb-4">
                               <div className="relative">
                                   <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none">🔍</span>
                                   <input
                                       ref={searchInputRef}
                                       type="text"
                                       defaultValue=""
                                       placeholder={`اكتب رقم الاختيار أو اسم ${currentField.title} ثم Enter...`}
                                       onInput={(e) => {
                                           searchValueRef.current = e.target.value;
                                           setSearchTerm(e.target.value);
                                       }}
                                       onKeyDown={(e) => {
                                           if (e.key === 'Enter') {
                                               e.preventDefault();
                                               const term = searchValueRef.current.trim();
                                               const filteredOptions = getFilteredOptions();
                                               const asNum = parseInt(term);
                                               let selectedOption = null;
                                               if (!isNaN(asNum) && String(asNum) === term) {
                                                   selectedOption = filteredOptions[asNum - 1];
                                               }
                                               if (!selectedOption && filteredOptions.length === 1) {
                                                   selectedOption = filteredOptions[0];
                                               }
                                               if (selectedOption) {
                                                   const newValue = selectedOption.value;
                                                   setQuickEntryData({ ...quickEntryData, [currentField.key]: newValue });
                                                   setSearchTerm('');
                                                   searchValueRef.current = '';
                                               if (searchInputRef.current) searchInputRef.current.value = '';
                                                   setSearchTerm('');
                                                   setQuickEntryError('');
                                                   setTimeout(() => {
                                                       if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                                           setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                                                       } else { onSubmit(); }
                                                   }, 100);
                                               } else if (term === '') {
                                                   setQuickEntryError('يرجى الاختيار من القائمة أو كتابة رقم');
                                               } else {
                                                   setQuickEntryError('لا يوجد تطابق واحد، تأكد من الاسم أو الرقم');
                                               }
                                           } else if (e.key === 'Escape') {
                                               onReset();
                                           }
                                       }}
                                       className="w-full text-right text-base p-3 pr-10 border-2 border-blue-500 rounded-lg focus:border-green-500 focus:ring-4 focus:ring-blue-100 transition-all bg-blue-50 font-medium"
                                       dir="rtl"
                                       autoComplete="off"
                                       autoFocus
                                   />
                               </div>
                               <p className="text-xs text-gray-500 mt-1 text-right">
                                   💡 رقم = اختيار مباشر • اسم = فلترة (نتيجة واحدة + Enter = اختيار تلقائي) • 📌 رمز التثبيت لإظهار الشركة دائماً في الأعلى
                               </p>
                           </div>
                           
                           {/* شبكة الخيارات */}
                           <div 
                               className="grid gap-2 border rounded-lg p-4 bg-gray-50" 
                               style={{ 
                                   gridTemplateColumns: 'repeat(6, 1fr)',
                                   maxHeight: 'calc(95vh - 300px)',
                                   overflowY: 'auto'
                               }}
                           >
                               {getFilteredOptions().map((option, index) => (
                                   <div
                                       key={option.value}
                                       onClick={() => {
                                        const newValue = option.value;
                                        setQuickEntryData({
                                            ...quickEntryData,
                                            [currentField.key]: newValue
                                        });
                                        
                                        setSearchTerm('');
                                        
                                        // الانتقال للحقل التالي مباشرة
                                        setTimeout(() => {
                                            if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                                setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                                            } else {
                                                onSubmit();
                                            }
                                        }, 100);
                                    }}
                                       className={`relative p-3 text-right rounded border transition-all duration-200 cursor-pointer hover:shadow-md ${
                                           quickEntryData[currentField.key] === option.value
                                               ? 'border-blue-500 bg-blue-100 text-blue-700 font-bold shadow-md'
                                               : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                       }`}
                                       style={{ minHeight: '80px' }}
                                   >
                                       {/* زر التثبيت */}
                                       {PIN_KEYS.includes(currentField.key) && (
                                           <button
                                               onClick={(e) => { e.stopPropagation(); togglePin(currentField.key, option.value); }}
                                               className={`absolute top-1 left-1 p-0.5 rounded hover:bg-yellow-100 transition-colors ${isPinned(currentField.key, option.value) ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                                               title={isPinned(currentField.key, option.value) ? 'إلغاء التثبيت' : 'تثبيت في الأعلى'}
                                           >
                                               <Pin size={14} fill={isPinned(currentField.key, option.value) ? 'currentColor' : 'none'} />
                                           </button>
                                       )}
                                       <div className="flex flex-col items-center">
                                           <span className="bg-blue-600 text-white px-2 py-1 rounded-full font-bold text-sm mb-2">
                                               {index + 1}
                                           </span>
                                           <span className="text-sm font-medium text-center leading-tight">
                                               {option.label.split('. ')[1]}
                                           </span>
                                       </div>
                                   </div>
                               ))}
                           </div>
                           
                           {getFilteredOptions().length === 0 && (
                               <div className="text-center text-gray-500 py-8">
                                   <p className="text-lg">لا توجد نتائج للبحث "{searchTerm}"</p>
                                   <button 
                                       onClick={() => setSearchTerm('')}
                                       className="mt-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                                   >
                                       مسح البحث
                                   </button>
                               </div>
                           )}
                       </div>
                   )}

                   {/* الاختيار المتعدد */}
                   {isMultiSelect && (
                       <div>
                           <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                               {currentField.title}
                               <div className="text-sm text-gray-500 mt-1">
                                   ابحث باسم الشركة أو رقمها وانقر للاختيار
                               </div>
                           </h3>
                           
                           <div className="mb-4">
                               <div className="relative">
                                   <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none">🔍</span>
                                   <input
                                       ref={searchInputRef}
                                       type="text"
                                       defaultValue=""
                                       placeholder="ابحث برقم الشركة أو اسمها..."
                                       onInput={(e) => { searchValueRef.current = e.target.value; setSearchTerm(e.target.value); }}
                                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onHandleKeyDown(e); } else if (e.key === 'Escape') { onReset(); } }}
                                       className="w-full text-right text-base p-3 pr-10 border-2 border-green-500 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-green-100 transition-all bg-green-50 font-medium"
                                       dir="rtl"
                                       autoFocus
                                   />
                               </div>
                               <p className="text-xs text-gray-500 mt-1 text-right">
                                   💡 اكتب رقماً أو جزءاً من الاسم للفلترة • انقر الشركات لاختيارها • 📌 رمز التثبيت لإظهارها دائماً في الأعلى • Enter للانتقال
                               </p>
                           </div>
                           
                           {/* شبكة الشركات */}
                           <div 
                               className="grid gap-2 border rounded-lg p-4 bg-gray-50" 
                               style={{ 
                                   gridTemplateColumns: 'repeat(6, 1fr)',
                                   maxHeight: 'calc(95vh - 350px)',
                                   overflowY: 'auto'
                               }}
                           >
                               {getFilteredOptions().map((option, index) => (
                                   <div
                                       key={option.value}
                                       onClick={() => {
                                           const currentArray = quickEntryData[currentField.key] || [];
                                           let newArray;
                                           
                                           if (currentArray.includes(option.value)) {
                                               newArray = currentArray.filter(item => item !== option.value);
                                           } else {
                                               newArray = [...currentArray, option.value];
                                           }
                                           
                                           setQuickEntryData({
                                               ...quickEntryData,
                                               [currentField.key]: newArray
                                           });
                                       }}
                                       className={`relative p-3 text-right rounded border transition-all duration-200 cursor-pointer hover:shadow-md ${
                                           (quickEntryData[currentField.key] || []).includes(option.value)
                                               ? 'border-green-500 bg-green-100 text-green-700 font-bold shadow-md'
                                               : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                                       }`}
                                       style={{ minHeight: '80px' }}
                                   >
                                       {/* زر التثبيت */}
                                       {PIN_KEYS.includes(currentField.key) && (
                                           <button
                                               onClick={(e) => { e.stopPropagation(); togglePin(currentField.key, option.value); }}
                                               className={`absolute top-1 left-1 p-0.5 rounded hover:bg-yellow-100 transition-colors ${isPinned(currentField.key, option.value) ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                                               title={isPinned(currentField.key, option.value) ? 'إلغاء التثبيت' : 'تثبيت في الأعلى'}
                                           >
                                               <Pin size={14} fill={isPinned(currentField.key, option.value) ? 'currentColor' : 'none'} />
                                           </button>
                                       )}
                                       <div className="flex flex-col items-center">
                                           <span className="bg-blue-600 text-white px-2 py-1 rounded-full font-bold text-sm mb-2">
                                               {index + 1}
                                           </span>
                                           <span className="text-sm font-medium text-center leading-tight">
                                               {(quickEntryData[currentField.key] || []).includes(option.value) && '✓ '}
                                               {option.label.split('. ')[1]}
                                           </span>
                                       </div>
                                   </div>
                               ))}
                           </div>
                           
                           {/* عداد الشركات المختارة */}
                           {quickEntryData[currentField.key] && quickEntryData[currentField.key].length > 0 && (
                               <div className="mt-3 bg-green-50 p-3 rounded-lg border border-green-200">
                                   <p className="text-sm text-green-700 font-medium text-center">
                                       ✅ تم اختيار {quickEntryData[currentField.key].length} شركة
                                   </p>
                               </div>
                           )}
                       </div>
                   )}

                 {/* الحقول العادية (مدة السفر، حالة الجواز، إلخ) */}
{(currentField?.type === 'select' && !isLargeField) && (
    <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {currentField.title}
            {currentField.required && <span className="text-red-500 mr-1">*</span>}
        </h3>
        {currentField.helpText && (
            <p className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2 mb-4 text-center">{currentField.helpText}</p>
        )}
        
        {/* حقل بحث موحد - رقم أو اسم */}
        <div className="mb-6">
            <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none">🔍</span>
                <input
                    ref={searchInputRef}
                    type="text"
                    defaultValue=""
                    placeholder={`اكتب رقم الاختيار أو اسم ${currentField.title} ثم Enter...`}
                    onInput={(e) => {
                        searchValueRef.current = e.target.value;
                        setSearchTerm(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            const term = searchValueRef.current.trim();
                            const options = currentField.options();
                            // الترتيب الفعلي المعروض (مع التثبيت)
                            const sortedOpts = PIN_KEYS.includes(currentField.key) ? getSortedWithPinned(currentField.key, options) : options;
                            const asNum = parseInt(term);
                            let filteredOpts = !term ? sortedOpts : sortedOpts.filter(o => {
                                if (!isNaN(asNum) && String(asNum) === term) return false;
                                return o.label.toLowerCase().includes(term.toLowerCase());
                            });
                            // بحث بالرقم بناءً على الترتيب الفعلي المعروض
                            let selectedOption = null;
                            if (!isNaN(asNum) && String(asNum) === term) {
                                selectedOption = sortedOpts[asNum - 1];
                            }
                            // نتيجة بحث واحدة
                            if (!selectedOption && filteredOpts.length === 1) selectedOption = filteredOpts[0];
                            // اختيار الكل إذا فارغ
                            if (!selectedOption && !term) {
                                setQuickEntryError('يرجى كتابة رقم الاختيار أو اسمه');
                                return;
                            }
                            if (selectedOption) {
                                setQuickEntryData({ ...quickEntryData, [currentField.key]: selectedOption.value });
                                searchValueRef.current = '';
                                if (searchInputRef.current) searchInputRef.current.value = '';
                                setSearchTerm('');
                                setQuickEntryError('');
                                setTimeout(() => {
                                    if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                        setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                                    } else { onSubmit(); }
                                }, 100);
                            } else if (term) {
                                setQuickEntryError('لا يوجد تطابق واحد، تأكد من الاسم أو الرقم');
                            }
                        } else if (e.key === 'Escape') {
                            onReset();
                        }
                    }}
                    className="w-full text-right text-xl p-4 pr-10 border-4 border-blue-500 rounded-lg focus:border-green-500 focus:ring-4 focus:ring-green-200 transition-all font-bold bg-blue-50"
                    dir="rtl"
                    autoFocus
                />
            </div>
            {PIN_KEYS.includes(currentField.key) && (
                <p className="text-xs text-gray-500 mt-1 text-right">💡 رقم = اختيار مباشر • اسم = فلترة • 📌 انقر رمز التثبيت لتثبيت الاختيار في الأعلى</p>
            )}
        </div>
        
        {/* عرض الخيارات مع التثبيت */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 max-h-80 overflow-y-auto border rounded-lg p-4 bg-gray-50">
            {(() => {
                const options = currentField.options();
                const filtered = !searchTerm ? options : options.filter(o => {
                    const asNum = parseInt(searchTerm.trim());
                    if (!isNaN(asNum) && String(asNum) === searchTerm.trim()) return true;
                    return `${o.label || ''} ${o.searchText || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
                });
                const sorted = PIN_KEYS.includes(currentField.key) ? getSortedWithPinned(currentField.key, filtered) : filtered;
                return sorted.map((option, index) => (
                    <div
                        key={option.value}
                        onClick={() => {
                            setQuickEntryData({ ...quickEntryData, [currentField.key]: option.value });
                            setSearchTerm('');
                            setQuickEntryError('');
                            setTimeout(() => {
                                if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                    setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                                } else { onSubmit(); }
                            }, 100);
                        }}
                        className={`relative p-3 text-center rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-lg ${
                            quickEntryData[currentField.key] === option.value
                                ? 'border-blue-500 bg-blue-50 text-blue-700 font-bold shadow-md'
                                : 'border-gray-200 bg-white hover:bg-blue-50'
                        }`}
                        style={{ minHeight: '60px' }}
                    >
                        {/* زر التثبيت للمهن */}
                        {PIN_KEYS.includes(currentField.key) && (
                            <button
                                onClick={(e) => { e.stopPropagation(); togglePin(currentField.key, option.value); }}
                                className={`absolute top-1 left-1 p-0.5 rounded hover:bg-yellow-100 transition-colors ${isPinned(currentField.key, option.value) ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                                title={isPinned(currentField.key, option.value) ? 'إلغاء التثبيت' : 'تثبيت في الأعلى'}
                            >
                                <Pin size={14} fill={isPinned(currentField.key, option.value) ? 'currentColor' : 'none'} />
                            </button>
                        )}
                        <div className="flex flex-col items-center justify-center h-full">
                            <span className="bg-blue-600 text-white px-2 py-1 rounded-full font-bold text-sm mb-1">
                                {index + 1}
                            </span>
                            <span className="text-sm font-medium leading-tight">
                                {option.label.split('. ')[1] || option.label}
                            </span>
                        </div>
                    </div>
                ));
            })()}
        </div>
    </div>
)}
                   {/* رقم الجواز */}
                   {currentField?.key === 'PassportNumber' && (
                       <div>
                           <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                               {currentField.title}
                               {currentField.required && <span className="text-red-500 mr-1">*</span>}
                           </h3>
                           
                           <div className="flex items-center justify-center gap-0 mb-4">
                               <div className="bg-blue-600 text-white text-3xl font-bold px-6 py-4 rounded-r-lg border-4 border-blue-600">
                                   A
                               </div>
                               <input
                                   type="text"
                                   value={(quickEntryData[currentField.key] || 'A').substring(1)}
                                   onChange={(e) => onHandleInput('A' + e.target.value)}
                                   onKeyDown={onHandleKeyDown}
                                   placeholder="12345678"
                                   className="text-center text-3xl font-bold p-4 border-4 border-l-0 border-blue-500 rounded-l-lg focus:border-green-500 focus:ring-4 focus:ring-green-200 transition-all bg-blue-50"
                                   maxLength={8}
                                   autoFocus
                                   dir="ltr"
                               />
                           </div>
                           
                           <div className="text-center text-gray-600">
                               <p className="text-lg">اكتب 8 أرقام فقط</p>
                               <p className="text-sm mt-2">
                                   النتيجة: <span className="font-bold text-blue-600 text-xl">
                                       {quickEntryData[currentField.key] || 'A________'}
                                   </span>
                               </p>
                           </div>
                       </div>
                   )}

                {/* الحقول النصية (عادي) */}
{currentField?.type === 'text' && currentField.key !== 'PassportNumber' && (
    <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {currentField.title}
            {currentField.required && <span className="text-red-500 mr-1">*</span>}
        </h3>
        {currentField.helpText && (
            <p className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2 mb-4 text-center">{currentField.helpText}</p>
        )}
        <input
            type="text"
            value={quickEntryData[currentField.key] || ''}
            onChange={(e) => onHandleInput(e.target.value)}
            onKeyDown={onHandleKeyDown}
            placeholder={currentField.placeholder}
            className="w-full text-center text-2xl p-4 border-4 border-blue-500 rounded-lg focus:border-green-500 focus:ring-4 focus:ring-green-200 transition-all font-bold bg-blue-50"
            dir={currentField.key === 'NameEnglish' ? 'ltr' : 'rtl'}
            autoFocus
            maxLength={currentField.key === 'NationalID' ? 14 : currentField.key === 'PassportProfessionText' ? 255 : undefined}
        />
        {currentField.key === 'NationalID' && quickEntryData[currentField.key] && quickEntryData[currentField.key].length === 14 && (
           <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
             <p className="text-lg text-blue-700 text-center font-medium">
                    ✓ سيتم حساب العمر وتاريخ الميلاد والمحافظة تلقائياً
                </p>
            </div>
        )}
    </div>
)}

{/* أرقام الهواتف (متعدد) - النسخة المحدثة */}
{currentField?.type === 'phone' && (
    <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {currentField.title}
            {currentField.required && <span className="text-red-500 mr-1">*</span>}
        </h3>
        
        {/* عرض الأرقام المحفوظة */}
        {quickEntryData[currentField.key] && quickEntryData[currentField.key].length > 0 && (
            <div className="mb-4 bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-700 mb-2 text-center">📱 الأرقام المضافة:</h4>
                <div className="flex flex-wrap gap-2 justify-center">
                    {(Array.isArray(quickEntryData[currentField.key]) ? quickEntryData[currentField.key] : [quickEntryData[currentField.key]])
                        .filter(phone => phone && phone.trim()) // تصفية الأرقام الفارغة
                        .map((phone, index) => (
                        <div 
                            key={index}
                            className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold border border-green-300 flex items-center gap-2"
                        >
                            <span>{phone}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    const currentPhones = Array.isArray(quickEntryData[currentField.key]) 
                                        ? quickEntryData[currentField.key] 
                                        : [quickEntryData[currentField.key]];
                                    const newPhones = currentPhones.filter((_, i) => i !== index);
                                    setQuickEntryData({
                                        ...quickEntryData,
                                        [currentField.key]: newPhones
                                    });
                                }}
                                className="text-red-500 hover:text-red-700 font-bold"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
                <p className="text-center text-green-600 text-sm mt-2">
                    المجموع: {(Array.isArray(quickEntryData[currentField.key]) ? quickEntryData[currentField.key] : [quickEntryData[currentField.key]])
                        .filter(phone => phone && phone.trim()).length} رقم
                </p>
            </div>
        )}
        
        {/* حقل إدخال رقم جديد */}
        <div className="mb-4">
            <input
                ref={phoneInputRef}
                type="text"
                placeholder="01xxxxxxxxx - أدخل رقم جديد واضغط Enter"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        const newPhone = e.target.value.trim();
                        
                        // التحقق من صحة الرقم
                        if (!newPhone) {
                            const currentPhones = quickEntryData[currentField.key];
                            const validPhones = currentPhones ? 
                                (Array.isArray(currentPhones) ? currentPhones : [currentPhones])
                                    .filter(phone => phone && phone.trim()) : [];
                            
                            if (validPhones.length > 0) {
                                // تحويل الأرقام للشكل المطلوب للنظام العادي
                                const phoneArray = [...validPhones, '', ''].slice(0, 3);
                                setQuickEntryData({
                                    ...quickEntryData,
                                    [currentField.key]: phoneArray
                                });
                                
                                setQuickEntryError('');
                                setTimeout(() => {
                                    if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                        setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                                    } else {
                                        onSubmit();
                                    }
                                }, 100);
                            } else {
                                setQuickEntryError('يجب إدخال رقم هاتف واحد على الأقل');
                            }
                            return;
                        }
                        
                        if (!/^01[0-9]{9}$/.test(newPhone)) {
                            setQuickEntryError('يجب أن يبدأ الرقم بـ 01 ويكون 11 رقم');
                            return;
                        }
                        
                        // التحقق من عدم التكرار
                        const currentPhones = quickEntryData[currentField.key];
                        if (currentPhones) {
                            const phonesArray = Array.isArray(currentPhones) ? currentPhones : [currentPhones];
                            const validPhones = phonesArray.filter(phone => phone && phone.trim());
                            
                            if (validPhones.includes(newPhone)) {
                                setQuickEntryError('هذا الرقم موجود بالفعل');
                                return;
                            }
                            
                            if (validPhones.length >= 3) {
                                setQuickEntryError('يمكن إضافة 3 أرقام كحد أقصى');
                                return;
                            }
                            
                            // إضافة الرقم الجديد
                            setQuickEntryData({
                                ...quickEntryData,
                                [currentField.key]: [...validPhones, newPhone]
                            });
                        } else {
                            // أول رقم
                            setQuickEntryData({
                                ...quickEntryData,
                                [currentField.key]: [newPhone]
                            });
                        }
                        
                        // مسح الحقل وإزالة الخطأ
                        e.target.value = '';
                        setQuickEntryError('');
                    } else if (e.key === 'Escape') {
                        onReset();
                    }
                }}
                className="w-full text-center text-2xl p-4 border-4 border-blue-500 rounded-lg focus:border-green-500 focus:ring-4 focus:ring-green-200 transition-all font-bold bg-blue-50"
                dir="ltr"
                autoFocus
                maxLength={11}
            />
        </div>
        
        <div className="text-center text-gray-600">
            <p className="text-lg">أدخل أرقام الهواتف واحداً تلو الآخر (حد أقصى 3 أرقام)</p>
            <p className="text-sm mt-2 font-bold text-blue-600">
                📱 Enter لإضافة رقم جديد | Enter على حقل فارغ للمتابعة
            </p>
            <div className="bg-blue-50 p-3 rounded-lg mt-3 border border-blue-200">
                <p className="text-blue-700 text-sm">
                    💡 يمكنك إضافة حتى 3 أرقام هواتف. أدخل الرقم واضغط Enter لإضافته
                </p>
            </div>
        </div>
    </div>
)}

        {/* حقول التاريخ */}
{currentField?.type === 'date' && (
    <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {currentField.title}
            {currentField.required && <span className="text-red-500 mr-1">*</span>}
        </h3>
        
        <div className="relative">
            <input
                type="text"
                value={(() => {
                    // تحويل من ISO format (yyyy-mm-dd) للعرض (dd/mm/yyyy) - نفس منطق formatDate في الملف
                    const value = quickEntryData[currentField.key] || '';
                    if (!value) return '';
                    
                    if (value.includes('-') && value.split('-').length === 3) {
                        // تنسيق قاعدة البيانات yyyy-mm-dd
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return '';
                        
                        const day = date.getDate();
                        const month = date.getMonth() + 1;
                        const year = date.getFullYear();
                        
                        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                    }
                    
                    return value; // إذا كان بتنسيق العرض أصلاً
                })()}
                onChange={(e) => {
                    let inputValue = e.target.value;
                    
                    // إزالة أي حروف غير رقمية أو شرطة مائلة
                    inputValue = inputValue.replace(/[^\d\/]/g, '');
                    
                    // إضافة الشرطة المائلة تلقائياً
                    if (inputValue.length === 2 && !inputValue.includes('/')) {
                        inputValue = inputValue + '/';
                    } else if (inputValue.length === 5 && inputValue.split('/').length === 2) {
                        inputValue = inputValue + '/';
                    }
                    
                    // منع كتابة أكثر من 10 أحرف (dd/mm/yyyy)
                    if (inputValue.length <= 10) {
                        // تحديث العرض فوراً
                        setQuickEntryData({
                            ...quickEntryData,
                            [currentField.key]: inputValue
                        });
                        
                        // التحويل لتنسيق قاعدة البيانات فقط إذا كان التنسيق مكتمل - نفس منطق parseDate
                        const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
                        const match = inputValue.match(datePattern);
                        
                        if (match) {
                            // استخراج القيم بالترتيب الصحيح: DD/MM/YYYY
                            const day = parseInt(match[1]);      // اليوم
                            const month = parseInt(match[2]);    // الشهر  
                            const year = parseInt(match[3]);     // السنة
                            
                            // فحص أساسي للصحة
                            if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                                // تكوين التاريخ بتنسيق قاعدة البيانات: YYYY-MM-DD
                                const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                
                                // التأكد من صحة التاريخ بإنشاء Date object
                                const testDate = new Date(year, month - 1, day);
                                if (testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                                    // حفظ بتنسيق قاعدة البيانات بس مؤقتاً للمعاينة
                                    console.log(`سيتم حفظ: ${isoDate} (من ${inputValue})`);
                                }
                            }
                        }
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        const displayValue = quickEntryData[currentField.key];
                        
                        // فحص التنسيق dd/mm/yyyy
                        const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
                        const match = displayValue?.match(datePattern);
                        
                        if (!displayValue) {
                            setQuickEntryError('تاريخ انتهاء الجواز مطلوب');
                            return;
                        }
                        
                        if (!match) {
                            setQuickEntryError('يرجى كتابة التاريخ بالتنسيق: dd/mm/yyyy');
                            return;
                        }
                        
                        // استخراج القيم بالترتيب الصحيح - نفس منطق parseDate في الملف
                        const day = parseInt(match[1]);      // اليوم
                        const month = parseInt(match[2]);    // الشهر  
                        const year = parseInt(match[3]);     // السنة
                        
                        // فحص صحة التاريخ
                        if (day < 1 || day > 31) {
                            setQuickEntryError('اليوم يجب أن يكون بين 1 و 31');
                            return;
                        }
                        
                        if (month < 1 || month > 12) {
                            setQuickEntryError('الشهر يجب أن يكون بين 1 و 12');
                            return;
                        }
                        
                        if (year < new Date().getFullYear()) {
                            setQuickEntryError('سنة انتهاء الجواز يجب أن تكون في المستقبل');
                            return;
                        }
                        
                        // التأكد من صحة التاريخ فعلياً
                        const testDate = new Date(year, month - 1, day);
                        if (testDate.getDate() !== day || testDate.getMonth() !== month - 1 || testDate.getFullYear() !== year) {
                            setQuickEntryError('التاريخ غير صحيح (تأكد من عدد الأيام في الشهر)');
                            return;
                        }
                        
                    
                        
                        setQuickEntryError('');
                        
                        // تحويل لتنسيق قاعدة البيانات YYYY-MM-DD - نفس منطق parseDate
                        const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        
                        console.log(`المستخدم أدخل: ${displayValue}`);
                        console.log(`اليوم=${day}, الشهر=${month}, السنة=${year}`);
                        console.log(`سيتم الحفظ في قاعدة البيانات: ${isoDate}`);
                        
                        // حفظ التاريخ بتنسيق قاعدة البيانات
                        setQuickEntryData({
                            ...quickEntryData,
                            [currentField.key]: isoDate
                        });
                        
                        // الانتقال للحقل التالي
                        setTimeout(() => {
                            if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                            } else {
                                onSubmit();
                            }
                        }, 100);
                    } else if (e.key === 'Escape') {
                        onReset();
                    }
                }}
                className="w-full text-2xl p-4 pr-12 border-4 border-blue-500 rounded-lg focus:border-green-500 focus:ring-4 focus:ring-green-200 transition-all font-bold bg-blue-50"
                dir="ltr"
                autoFocus
                maxLength={10}
                placeholder="DD/MM/YYYY"
            />
            
            {/* أيقونة التقويم */}
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 pointer-events-none">
                <Calendar size={24} />
            </div>
        </div>
        
        {/* معاينة التاريخ المدخل */}
        {quickEntryData[currentField.key] && (
            <div className="mt-4">
                {(() => {
                    const value = quickEntryData[currentField.key];
                    let displayDate = '';
                    let arabicDate = '';
                    let isValidFormat = false;
                    
                    // استخدام نفس منطق formatDate في الملف
                    if (value.includes('-') && value.split('-').length === 3) {
                        // تنسيق قاعدة البيانات yyyy-mm-dd
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            const day = date.getDate();
                            const month = date.getMonth() + 1;
                            const year = date.getFullYear();
                            
                            displayDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                            arabicDate = date.toLocaleDateString('ar-EG', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            });
                            isValidFormat = true;
                        }
                    } else if (value.includes('/')) {
                        // تنسيق العرض dd/mm/yyyy
                        displayDate = value;
                        const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
                        const match = value.match(datePattern);
                        
                        if (match) {
                            const day = parseInt(match[1]);
                            const month = parseInt(match[2]);
                            const year = parseInt(match[3]);
                            
                            const testDate = new Date(year, month - 1, day);
                            if (testDate.getDate() === day && testDate.getMonth() === month - 1) {
                                arabicDate = testDate.toLocaleDateString('ar-EG', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                });
                                isValidFormat = true;
                            }
                        }
                    }
                    
                    return (
                        <div className={`p-4 rounded-lg border-2 ${isValidFormat ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
                            <div className="text-center">
                                <p className="text-lg font-bold mb-2">
                                    📅 <span className="text-blue-600">التاريخ المدخل:</span> {displayDate}
                                </p>
                                
                                {isValidFormat && arabicDate && (
                                    <p className="text-green-700 font-bold text-xl">
                                        ✅ {arabicDate}
                                    </p>
                                )}
                                
                                {!isValidFormat && value && (
                                    <p className="text-yellow-700 font-medium">
                                        ⚠️ أكمل كتابة التاريخ بالتنسيق: dd/mm/yyyy
                                    </p>
                                )}
                                
                                {/* تفاصيل التاريخ */}
                                {isValidFormat && (
                                    <div className="mt-3 text-sm bg-white p-3 rounded border">
                                        <p><strong>للحفظ في قاعدة البيانات:</strong> {value.includes('-') ? value : (() => {
                                            const [d, m, y] = displayDate.split('/');
                                            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
                                        })()}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>
        )}
    </div>
)}
              {/* حقل الصور المدمج */}
{currentField?.type === 'images' && (
    <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {currentField.title}
        </h3>
        
        <div className="mb-4">
            <input
                key={`images-input-${quickEntryStep}`} // مفتاح فريد لإعادة إنشاء الحقل
                type="text"
                placeholder="اضغط Enter للانتقال للملاحظات أو Esc للإلغاء"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        setQuickEntryError('');
                        
                        // الانتقال للحقل التالي
                        setTimeout(() => {
                            if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                            } else {
                                onSubmit();
                            }
                        }, 100);
                    }
                    
                    if (e.key === 'Escape') {
                        onReset();
                    }
                }}
                onFocus={(e) => {
                    // إعادة التركيز عند فقدانه
                    setTimeout(() => {
                        if (document.activeElement !== e.target) {
                            e.target.focus();
                        }
                    }, 100);
                }}
                className="w-full text-center p-3 border-2 border-blue-500 rounded-lg focus:border-green-500 bg-blue-50 font-bold"
                autoFocus
            />
        </div>
        
        <div className="text-center mb-6">
            <div className="bg-green-50 p-4 rounded-lg mb-6 border border-green-200">
                <p className="text-green-700 font-bold text-lg mb-2">⚡ اضغط Enter أعلاه للانتقال للملاحظات</p>
                <p className="text-green-600 text-sm">أو اختر الصور أولاً ثم اضغط Enter</p>
                <div className="text-xs text-gray-600 mt-2 bg-white p-2 rounded">
                    الخطوة {quickEntryStep + 1} من {getVisibleQuickEntryFields().length}
                    {quickEntryStep + 1 < getVisibleQuickEntryFields().length ? ' - التالي: الملاحظات' : ' - الأخيرة: حفظ'}
                </div>
            </div>
            
            {/* شبكة الصور */}
            <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
                
                {/* صورة الجواز */}
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 transition-all">
                    <h4 className="font-bold text-gray-700 mb-3 text-center">📘 صورة الجواز</h4>
                    
                    {quickEntryData.PassportImage ? (
                        <div className="text-center">
                            <img 
                                src={quickEntryData.PassportImage} 
                                alt="صورة الجواز"
                                className="w-full h-32 object-cover rounded-lg border mb-3"
                            />
                            <div className="flex gap-2 justify-center">
                                <label className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setQuickEntryData({
                                                        ...quickEntryData,
                                                        PassportImage: reader.result
                                                    });
                                                    
                                                    // إعادة التركيز للحقل النصي
                                                    setTimeout(() => {
                                                        const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                        if (textInput) {
                                                            textInput.focus();
                                                        }
                                                    }, 200);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    تغيير
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQuickEntryData({
                                            ...quickEntryData,
                                            PassportImage: ''
                                        });
                                        
                                        // إعادة التركيز للحقل النصي
                                        setTimeout(() => {
                                            const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                            if (textInput) {
                                                textInput.focus();
                                            }
                                        }, 100);
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                >
                                    حذف
                                </button>
                            </div>
                        </div>
                    ) : (
                        <label className="cursor-pointer block">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setQuickEntryData({
                                                ...quickEntryData,
                                                PassportImage: reader.result
                                            });
                                            
                                            // إعادة التركيز للحقل النصي
                                            setTimeout(() => {
                                                const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                if (textInput) {
                                                    textInput.focus();
                                                }
                                            }, 200);
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                                className="hidden"
                            />
                            <div className="text-center py-8">
                                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-sm text-gray-600">انقر لاختيار</p>
                            </div>
                        </label>
                    )}
                </div>

                {/* الصورة الشخصية */}
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-green-500 transition-all">
                    <h4 className="font-bold text-gray-700 mb-3 text-center">👤 الصورة الشخصية</h4>
                    
                    {quickEntryData.PersonalImage ? (
                        <div className="text-center">
                            <img 
                                src={quickEntryData.PersonalImage} 
                                alt="الصورة الشخصية"
                                className="w-full h-32 object-cover rounded-lg border mb-3"
                            />
                            <div className="flex gap-2 justify-center">
                                <label className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setQuickEntryData({
                                                        ...quickEntryData,
                                                        PersonalImage: reader.result
                                                    });
                                                    
                                                    // إعادة التركيز للحقل النصي
                                                    setTimeout(() => {
                                                        const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                        if (textInput) {
                                                            textInput.focus();
                                                        }
                                                    }, 200);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    تغيير
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQuickEntryData({
                                            ...quickEntryData,
                                            PersonalImage: ''
                                        });
                                        
                                        // إعادة التركيز للحقل النصي
                                        setTimeout(() => {
                                            const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                            if (textInput) {
                                                textInput.focus();
                                            }
                                        }, 100);
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                >
                                    حذف
                                </button>
                            </div>
                        </div>
                    ) : (
                        <label className="cursor-pointer block">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setQuickEntryData({
                                                ...quickEntryData,
                                                PersonalImage: reader.result
                                            });
                                            
                                            // إعادة التركيز للحقل النصي
                                            setTimeout(() => {
                                                const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                if (textInput) {
                                                    textInput.focus();
                                                }
                                            }, 200);
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                                className="hidden"
                            />
                            <div className="text-center py-8">
                                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-sm text-gray-600">انقر لاختيار</p>
                            </div>
                        </label>
                    )}
                </div>

                {/* صورة الرخصة */}
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-yellow-500 transition-all">
                    <h4 className="font-bold text-gray-700 mb-3 text-center">🚗 صورة الرخصة</h4>
                    
                    {quickEntryData.LicenseImage ? (
                        <div className="text-center">
                            <img 
                                src={quickEntryData.LicenseImage} 
                                alt="صورة الرخصة"
                                className="w-full h-32 object-cover rounded-lg border mb-3"
                            />
                            <div className="flex gap-2 justify-center">
                                <label className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setQuickEntryData({
                                                        ...quickEntryData,
                                                        LicenseImage: reader.result
                                                    });
                                                    
                                                    // إعادة التركيز للحقل النصي
                                                    setTimeout(() => {
                                                        const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                        if (textInput) {
                                                            textInput.focus();
                                                        }
                                                    }, 200);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    تغيير
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQuickEntryData({
                                            ...quickEntryData,
                                            LicenseImage: ''
                                        });
                                        
                                        // إعادة التركيز للحقل النصي
                                        setTimeout(() => {
                                            const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                            if (textInput) {
                                                textInput.focus();
                                            }
                                        }, 100);
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                >
                                    حذف
                                </button>
                            </div>
                        </div>
                    ) : (
                        <label className="cursor-pointer block">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setQuickEntryData({
                                                ...quickEntryData,
                                                LicenseImage: reader.result
                                            });
                                            
                                            // إعادة التركيز للحقل النصي
                                            setTimeout(() => {
                                                const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                if (textInput) {
                                                    textInput.focus();
                                                }
                                            }, 200);
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                                className="hidden"
                            />
                            <div className="text-center py-8">
                                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-sm text-gray-600">انقر لاختيار</p>
                            </div>
                        </label>
                    )}
                </div>

                {/* صورة البطاقة */}
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-500 transition-all">
                    <h4 className="font-bold text-gray-700 mb-3 text-center">🆔 صورة البطاقة</h4>
                    
                    {quickEntryData.IDImage ? (
                        <div className="text-center">
                            <img 
                                src={quickEntryData.IDImage} 
                                alt="صورة البطاقة"
                                className="w-full h-32 object-cover rounded-lg border mb-3"
                            />
                            <div className="flex gap-2 justify-center">
                                <label className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setQuickEntryData({
                                                        ...quickEntryData,
                                                        IDImage: reader.result
                                                    });
                                                    
                                                    // إعادة التركيز للحقل النصي
                                                    setTimeout(() => {
                                                        const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                        if (textInput) {
                                                            textInput.focus();
                                                        }
                                                    }, 200);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    تغيير
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQuickEntryData({
                                            ...quickEntryData,
                                            IDImage: ''
                                        });
                                        
                                        // إعادة التركيز للحقل النصي
                                        setTimeout(() => {
                                            const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                            if (textInput) {
                                                textInput.focus();
                                            }
                                        }, 100);
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                >
                                    حذف
                                </button>
                            </div>
                        </div>
                    ) : (
                        <label className="cursor-pointer block">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setQuickEntryData({
                                                ...quickEntryData,
                                                IDImage: reader.result
                                            });
                                            
                                            // إعادة التركيز للحقل النصي
                                            setTimeout(() => {
                                                const textInput = document.querySelector(`input[placeholder*="Enter للانتقال"]`);
                                                if (textInput) {
                                                    textInput.focus();
                                                }
                                            }, 200);
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                                className="hidden"
                            />
                            <div className="text-center py-8">
                                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-sm text-gray-600">انقر لاختيار</p>
                            </div>
                        </label>
                    )}
                </div>
          {/* رخصة (خلفي) */}
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-yellow-400 transition-all">
                    <h4 className="font-bold text-gray-700 mb-3 text-center">🚗 رخصة (خلفي)</h4>
                    {quickEntryData.LicenseBackImage ? (
                        <div className="text-center">
                            <img src={quickEntryData.LicenseBackImage} alt="رخصة خلفي" className="w-full h-32 object-cover rounded-lg border mb-3"/>
                            <div className="flex gap-2 justify-center">
                                <label className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                    <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setQuickEntryData({...quickEntryData, LicenseBackImage: reader.result}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 200); }; reader.readAsDataURL(file); }}} className="hidden"/>
                                    تغيير
                                </label>
                                <button type="button" onClick={() => { setQuickEntryData({...quickEntryData, LicenseBackImage: ''}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 100); }} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">حذف</button>
                            </div>
                        </div>
                    ) : (
                        <label className="cursor-pointer block">
                            <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setQuickEntryData({...quickEntryData, LicenseBackImage: reader.result}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 200); }; reader.readAsDataURL(file); }}} className="hidden"/>
                            <div className="text-center py-8">
                                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                                <p className="text-sm text-gray-600">انقر لاختيار</p>
                            </div>
                        </label>
                    )}
                </div>

                {/* بطاقة (خلفي) */}
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 transition-all">
                    <h4 className="font-bold text-gray-700 mb-3 text-center">🆔 بطاقة (خلفي)</h4>
                    {quickEntryData.IDBackImage ? (
                        <div className="text-center">
                            <img src={quickEntryData.IDBackImage} alt="بطاقة خلفي" className="w-full h-32 object-cover rounded-lg border mb-3"/>
                            <div className="flex gap-2 justify-center">
                                <label className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                    <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setQuickEntryData({...quickEntryData, IDBackImage: reader.result}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 200); }; reader.readAsDataURL(file); }}} className="hidden"/>
                                    تغيير
                                </label>
                                <button type="button" onClick={() => { setQuickEntryData({...quickEntryData, IDBackImage: ''}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 100); }} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">حذف</button>
                            </div>
                        </div>
                    ) : (
                        <label className="cursor-pointer block">
                            <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setQuickEntryData({...quickEntryData, IDBackImage: reader.result}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 200); }; reader.readAsDataURL(file); }}} className="hidden"/>
                            <div className="text-center py-8">
                                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                                <p className="text-sm text-gray-600">انقر لاختيار</p>
                            </div>
                        </label>
                    )}
                </div>
            </div>

            {/* المستندات PDF */}
            <div className="mt-6">
                <h4 className="font-bold text-gray-700 mb-3 text-center">📄 المستندات</h4>
                <div className="grid grid-cols-2 gap-4">
                    {[
                        { key: 'CompanyContract', title: 'عقد الشركة', icon: '🏢' },
                        { key: 'MinistryContract', title: 'عقد الوزارة', icon: '🏛️' },
                        { key: 'VisaFile', title: 'التأشيرة', icon: '✈️' },
                        { key: 'OtherAttachments', title: 'مرفقات أخرى', icon: '📎' },
                    ].map(({ key, title, icon }) => (
                        <div key={key} className="bg-gray-50 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 transition-all">
                            <h4 className="font-bold text-gray-700 mb-2 text-center text-sm">{icon} {title}</h4>
                            {quickEntryData[key] ? (
                                <div className="text-center">
                                    <div className="bg-green-100 text-green-700 px-3 py-2 rounded mb-2 text-sm">✅ تم الرفع</div>
                                    <div className="flex gap-2 justify-center">
                                        <label className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                            <input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setQuickEntryData({...quickEntryData, [key]: reader.result}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 200); }; reader.readAsDataURL(file); }}} className="hidden"/>
                                            تغيير
                                        </label>
                                        <button type="button" onClick={() => { setQuickEntryData({...quickEntryData, [key]: ''}); }} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">حذف</button>
                                    </div>
                                </div>
                            ) : (
                                <label className="cursor-pointer block">
                                    <input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setQuickEntryData({...quickEntryData, [key]: reader.result}); setTimeout(() => { const t = document.querySelector(`input[placeholder*="Enter للانتقال"]`); if(t) t.focus(); }, 200); }; reader.readAsDataURL(file); }}} className="hidden"/>
                                    <div className="text-center py-4">
                                        <svg className="w-8 h-8 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                                        <p className="text-xs text-gray-600">انقر لاختيار</p>
                                    </div>
                                </label>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            
            {/* إحصائيات الصور المرفوعة */}
            <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-blue-700 font-bold text-center">
                   📊 تم رفع {[
                        quickEntryData.PassportImage,
                        quickEntryData.PersonalImage,
                        quickEntryData.LicenseImage,
                        quickEntryData.LicenseBackImage,
                        quickEntryData.IDImage,
                        quickEntryData.IDBackImage,
                        quickEntryData.CompanyContract,
                        quickEntryData.MinistryContract,
                        quickEntryData.VisaFile,
                        quickEntryData.OtherAttachments
                    ].filter(Boolean).length} من 10 مرفقات
                </p>
                <p className="text-blue-600 text-sm text-center mt-2">
                    ⬆️ اضغط Enter في الحقل أعلاه للمتابعة
                </p>
            </div>
            
            {/* زر للانتقال المباشر */}
            <div className="mt-4">
                <button
                    onClick={() => {
                        setQuickEntryError('');
                        setTimeout(() => {
                            if (quickEntryStep < getVisibleQuickEntryFields().length - 1) {
                                setSearchTerm(''); setQuickEntryStep(quickEntryStep + 1);
                            } else {
                                onSubmit();
                            }
                        }, 100);
                    }}
                    className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-bold shadow-lg transition"
                >
                    ⚡ الانتقال للملاحظات
                </button>
            </div>
        </div>
    </div>
)}
                 {/* الملاحظات */}
{currentField?.type === 'textarea' && (
    <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {currentField.title}
            {currentField.required && <span className="text-red-500 mr-1">*</span>}
        </h3>
        
        <div className="bg-green-50 p-4 rounded-lg mb-6 border border-green-200">
            <p className="text-green-700 font-bold text-lg text-center mb-2">🎯 الخطوة الأخيرة!</p>
            <p className="text-green-600 text-sm text-center">اكتب الملاحظة ثم اضغط Enter للحفظ والإنهاء</p>
        </div>
        
        <textarea
            value={quickEntryData[currentField.key] || ''}
            onChange={(e) => {
                setQuickEntryData({
                    ...quickEntryData,
                    [currentField.key]: e.target.value
                });
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    setQuickEntryError('');
                    
                    // تأكيد الحفظ
                    console.log('حفظ من الملاحظات');
                    setTimeout(() => {
                        onSubmit(); // استدعاء دالة الحفظ مباشرة
                    }, 100);
                } else if (e.key === 'Escape') {
                    onReset();
                }
            }}
            placeholder={currentField.placeholder + " | اضغط Enter للحفظ | Shift+Enter لسطر جديد"}
            className="w-full text-center text-xl p-4 border-4 border-green-500 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-200 transition-all resize-none font-bold bg-green-50"
            rows="4"
            dir="rtl"
            autoFocus
        />
        
        <div className="text-center text-gray-600 mt-2">
            <p className="text-lg font-bold text-green-600">⚡ Enter = حفظ | Shift+Enter = سطر جديد</p>
        </div>
        
        {/* زر حفظ إضافي */}
        <div className="mt-4 text-center">
            <button
                onClick={() => {
                    setQuickEntryError('');
                    onSubmit();
                }}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-8 py-3 rounded-lg font-bold shadow-lg transition"
            >
                💾 حفظ الجواز
            </button>
        </div>
    </div>
)}

                   {quickEntryError && (
                       <div className="mt-4 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                           <p className="text-red-600 text-center font-bold">
                               ⚠️ {quickEntryError}
                           </p>
                       </div>
                   )}

                   {/* أزرار التحكم */}
                   <div className="flex justify-between items-center pt-3 border-t border-gray-200 mt-4">
                       <button
                           onClick={onGoToPrevious}
                           disabled={quickEntryStep === 0}
                           className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                       >
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H3m0 0l6-6m-6 6l6 6" />
                           </svg>
                           السابق
                       </button>

                       <div className="text-center">
                           <div className="text-sm text-gray-600 font-bold">
                               {quickEntryStep === visibleFields.length - 1 ? 
                                   '🎯 Enter للحفظ • Esc للإلغاء' : 
                                   '⚡ Enter للتالي • Esc للإلغاء'
                               }
                           </div>
                       </div>

                       <button
                           onClick={() => {
                               // حفظ قيمة الحقل الحالي قبل الانتقال
                               if (currentField?.type === 'text' || currentField?.key === 'PassportNumber') {
                                   // الحقول النصية - القيمة محفوظة تلقائياً عبر onChange
                                   // لكن نتحقق إذا كان هناك input نشط لم يتم حفظه
                                   const activeInput = document.activeElement;
                                   if (activeInput && (activeInput.tagName === 'INPUT' || activeInput.tagName === 'TEXTAREA')) {
                                       const val = activeInput.value;
                                       if (val && val !== (quickEntryData[currentField.key] || '')) {
                                           setQuickEntryData({ ...quickEntryData, [currentField.key]: val });
                                       }
                                   }
                               } else if (currentField?.type === 'phone') {
                                   // حقل الهاتف - إذا كان فيه رقم في الـ input لم يُضغط عليه Enter
                                   const phoneInput = phoneInputRef.current;
                                   const newPhone = phoneInput ? phoneInput.value.trim() : '';
                                   const currentPhones = quickEntryData[currentField.key];
                                   const existingPhones = currentPhones 
                                       ? (Array.isArray(currentPhones) ? currentPhones : [currentPhones]).filter(p => p && p.trim())
                                       : [];
                                   
                                   if (newPhone && /^01[0-9]{9}$/.test(newPhone)) {
                                       // يوجد رقم صحيح في الحقل - أضفه قبل الانتقال
                                       if (!existingPhones.includes(newPhone) && existingPhones.length < 3) {
                                           const updated = [...existingPhones, newPhone];
                                           setQuickEntryData({ ...quickEntryData, [currentField.key]: [...updated, '', ''].slice(0, 3) });
                                           if (phoneInput) phoneInput.value = '';
                                       }
                                   } else if (existingPhones.length > 0) {
                                       // تأكد من حفظ الأرقام المضافة مسبقاً بالشكل الصحيح
                                       setQuickEntryData({ ...quickEntryData, [currentField.key]: [...existingPhones, '', ''].slice(0, 3) });
                                   }
                               }
                               setTimeout(() => onGoToNext(), 50);
                           }}
                           disabled={isLoading}
                           className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 font-bold shadow-lg"
                       >
                           {quickEntryStep === visibleFields.length - 1 ? (
                               <>
                                   <Save size={16} />
                                   🎯 حفظ
                               </>
                           ) : (
                               <>
                                   ⚡ التالي
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h12m0 0l-6-6m6 6l-6 6" />
                                   </svg>
                               </>
                           )}
                       </button>
                   </div>
               </div>
           </div>
       </div>
   );
};


export { FieldMessage, NotesSection };